import { Helpers } from './helpers/src/helpers.js';
import { FrameSize } from './frame-size.js';
import { Key } from './key.js';
import { Mouse } from './mouse.js';
import { Range } from './range/src/range.js';
import { Content } from './content.js';
import { ContentChecker } from './content-checker.js';
import { ContentCleaner } from './content-cleaner.js';
import { Tidy } from './tidy.js';
import { UndoRedo } from './undo-redo.js';
import { Style } from './style/src/style.js';
import { Font } from './font/src/font.js';
import { CopyPaste } from './copy-paste/src/copy-paste.js';
import { Image } from './image/src/image.js';
import { Document } from './document/src/document.js';
import { Link } from './link/src/link.js';
import { Anchor } from './anchor/src/anchor.js';
import { Table } from './table/src/table.js';
import { Layer } from './layer/src/layer.js';
import { ArtText } from './art-text/src/art-text.js';
import { Divider } from './divider/src/divider.js';
import { HTML } from './html/src/html.js';
import { EmailMacros } from './email-macros/src/email-macros.js';


if (!BEditor.Editor)
{
  BEditor.Editor = Editor;
}


export default function Editor(pParent, editorId, idIn, idOut, blankPageURL, displayMode, pToolbar, pToolbarConfig, autoStart, autoFocus, minEditorHeight, mainIdForBodyStyles, mainClassForBodyStyles, extParameters)
{
  const pThis = this;
  const setTimeout = BEditor.createRegistrableTimeout();

  pThis.EditorStarted = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorStarted' });
  pThis.EditorStopped = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorStopped' });
  pThis.EditorResized = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorResized' });
  pThis.EditorClicked = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorClicked' });
  pThis.EditorBlured = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorBlured' });
  pThis.EditorFocus = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorFocus' });
  pThis.EditorBlur = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorBlur' });
  pThis.EditorReady = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorReady' });
  pThis.EditorDataChanged = BEditor.eventHandlers.createHandlers(pThis, { id: 'EditorDataChanged' });

  pThis.isObjExist = true;
  pThis.isObjValid = false;
  pThis.isObjStay = false;
  pThis.isObjDeleted = false;

  pThis.getEditorContainer = () => cutBox;
  pThis.getEditorIFrame = () => iFrame;
  pThis.getCurrentBlockFormatting = (...args) => style.blockFormatter.getCurrentBlockFormat(...args);
  pThis.getCurrentTextFont = (...args) => font.family.getCurrentTextFont(...args);
  pThis.getCurrentFontSize = (...args) => font.size.getCurrentFontSize(...args);
  pThis.getCurrentLineHeight = (...args) => style.lineHeight.getCurrentLineHeight(...args);
  pThis.setFontSize = (...args) => font.size.setFontSize(...args);
  pThis.setEditorSize = (...args) => frameSize.setSize(...args);
  pThis.setLineHeight = (...args) => style.lineHeight.setLineHeight(...args);
  pThis.clearSelection = (...args) => range.selection.clear(...args);
  pThis.isRangeElementPositionAbsolute = (...args) => layer.helpers.isRangeElementPositionAbsolute(...args);
  pThis.stopEdit = stopEdit;
  pThis.continueEditing = continueEditing;
  pThis.getIsContentChanged = () => undoRedo.isContentChanged;
  pThis.closeDropdown = closeDropdown;
  pThis.setIsDropdownOpen = setIsDropdownOpen;
  pThis.setImageProperty = (...args) => image.ieditor.setImageProperty(...args);
  pThis.onImageSettingsToolBarClosed = (...args) => image.ieditor.onImageSettingsToolBarClosed(...args);
  pThis.setIsPersistEditing = function () { isPersistEditing = true; isExternalPersistEditing = true; };
  pThis.changeBodyCSSClasses = changeBodyCSSClasses;
  pThis.revertAutoCleaning = (...args) => copyPaste.revertAutoCleaning(...args);
  pThis.hideAutoCleaningMessage = (...args) => copyPaste.hideAutoCleaningMessage(...args);
  pThis.getData = (...args) => content.getData(...args);
  pThis.getTextData = (...args) => content.getTextData(...args);
  pThis.getHtmlDataLength = (...args) => content.getHtmlDataLength(...args);
  pThis.getTextDataLength = (...args) => content.getTextDataLength(...args);
  pThis.getHtmlData = (...args) => content.getHtmlData(...args);
  pThis.saveData = (...args) => content.saveData(...args);
  pThis.unload = unload;
  pThis.deleteEditor = deleteEditor;
  pThis.disposeEditor = disposeEditorDo;
  pThis.isReactComponentEditor = () => !!extParameters.isReactComponentEditor;
  pThis.addHandler = addHandler;
  pThis.removeHandler = removeHandler;

  pThis.setEditorWidth = (...args) => frameSize.setEditorWidth(...args);
  pThis.setEditorHeight = (...args) => frameSize.setEditorHeight(...args);

  pThis.saveEditorRange = (...args) => range.save(...args);
  pThis.restoreEditorRange = (...args) => range.restore(...args);

  pThis.reloadUrlParameter = null;

  blankPageURL += BEditor.themeResourcesPostfix;

  const wa = WA;

  let isAdminComponent = (WA && BEditor.AdminPanel && typeof(BEditor.AdminPanel.getContentAreaType) === 'function' && BEditor.AdminPanel.getContentAreaType() === "adminComponent"),
      isReactComponentActive = (isAdminComponent && WA.ReactiveEmailingActive) || extParameters.isReactComponentEditor,
      $winContent = (BEditor.environment.topWindow.contentarea && !(isAdminComponent || isReactComponentActive)) ? BEditor.environment.topWindow.contentarea : window;

  const handlerList =
  {
    'inited': [],
    'starting': [],
    'started': [],
    'focus': [],
    'blur': [],
    'unreged': [],
    'imageControlMouseOver' : [],
    'imageControlMouseOut' : []
  };

  const helpers = new Helpers(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get range() { return range; },
    get content() { return content; },
    get contentCleaner() { return contentCleaner; },
    get contentChecker() { return contentChecker; },
    get undoRedo() { return undoRedo; },
    get style() { return style; },
    get html() { return html; },
    get image() { return image; },
    get anchor() { return anchor; },
    get layer() { return layer; },

    get editorId() { return editorId; },
    get blankPageURL() { return blankPageURL; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get iFrameName() { return iFrameName; },
    get iFrameId() { return iFrameId; },
    get objIFrame() { return objIFrame; },
    get cutBoxCup() { return cutBoxCup; },
    get cutBoxId() { return cutBoxId; },
    get $winContent() { return $winContent; },

    get isEditorInViewMode() { return isEditorInViewMode; },
    get bodyStyles() { return bodyStyles; },
    get waitIFrameCounter() { return waitIFrameCounter; },
    set waitIFrameCounter(v) { waitIFrameCounter = v; },
    get checkAllBlockTags() { return checkAllBlockTags; },
    get checkAllBlockTagsExt() { return checkAllBlockTagsExt; },
    get checkAllBlockTagsBoth() { return checkAllBlockTagsBoth; },
    get checkThTdTags() { return checkThTdTags; },

    persistEditing,
    resetPersistEditing


  });



  const frameSize = new FrameSize(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get content() { return content; },

    get extParameters() { return extParameters; },
    get iFrameId() { return iFrameId; },
    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get iFrameHtml() { return iFrameHtml; },
    get objIFrame() { return objIFrame; },
    get cutBox() { return cutBox; },
    get cutBoxCup() { return cutBoxCup; },
    get htmlElement() { return htmlElement; },
    get inContainer() { return inContainer; },
    get parentContainer() { return parentContainer; },
    get isMultiEditorMode() { return isMultiEditorMode; },
    get $winContent() { return $winContent; },

    get $() { return $; },
    get $Style() { return $Style; },
    get $StyleInt() { return $StyleInt; },
    get isEditorInViewMode() { return isEditorInViewMode; },
    get editorMinHeight() { return editorMinHeight; },
    set editorMinHeight(v) { editorMinHeight = v; },
    get isAbsElementMoving() { return isAbsElementMoving; },
    get isGadget() { return isGadget; },
    get cloneEditableArea() { return cloneEditableArea; }


  });



  const key = new Key(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },
    get frameSize() { return frameSize; },
    get contentChecker() { return contentChecker; },
    get undoRedo() { return undoRedo; },
    get copyPaste() { return copyPaste; },
    get style() { return style; },
    get layer() { return layer; },
    get anchor() { return anchor; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },

    get isEditorInHeader() { return isEditorInHeader; },
    get checkAllBlockTags() { return checkAllBlockTags; },

    extRefreshState


  });



  const mouse = new Mouse(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },
    get pParent() { return pParent },

    get helpers() { return helpers; },
    get frameSize() { return frameSize; },
    get key() { return key; },
    get range() { return range; },
    get contentChecker() { return contentChecker; },
    get undoRedo() { return undoRedo; },
    get copyPaste() { return copyPaste; },
    get font() { return font; },
    get style() { return style; },
    get image() { return image; },
    get link() { return link; },
    get anchor() { return anchor; },
    get layer() { return layer; },

    get iFrameDoc() { return iFrameDoc; },
    get $winContent() { return $winContent; },
    get isMultiEditorMode() { return isMultiEditorMode; },

    get isEditorInHeader() { return isEditorInHeader; },
    get isDropdownOpen() { return isDropdownOpen; },
    set isDropdownOpen(v) { isDropdownOpen = v; },
    get $Style() { return $Style; },

    moveParagraphCursorBeforeSpace


  });



  const range = new Range(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get undoRedo() { return undoRedo; },
    get font() { return font; },
    get image() { return image; },
    get layer() { return layer; },
    get link() { return link; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get $Style() { return $Style; },
    get isFocused() { return isFocused; },
    get checkAllBlockTags() { return checkAllBlockTags; },

    isChildrenOnlyTextNode,
    isOnlyTargetChildNode


  });



  const content = new Content(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get contentCleaner() { return contentCleaner; },
    get anchor() { return anchor; },

    get extParameters() { return extParameters; },
    get idIn() { return idIn; },
    get idOut() { return idOut; },
    get iFrameDoc() { return iFrameDoc; },
    get iFrameId() { return iFrameId; },
    get inContainer() { return inContainer; },
    get $winContent() { return $winContent; },

    get isMultiEditorMode() { return isMultiEditorMode; },
    get displayMode() { return displayMode; },

    removeWaContentMarker


  });



  const contentChecker = new ContentChecker(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get contentCleaner() { return contentCleaner; },
    get undoRedo() { return undoRedo; },
    get style() { return style; },
    get image() { return image; },
    get anchor() { return anchor; },
    get layer() { return layer; },

    get iFrameDoc() { return iFrameDoc; },


    get isTidyServiceOn() { return isTidyServiceOn; },
    get repairAndCleanHtml() { return repairAndCleanHtml; }


  });



  const contentCleaner = new ContentCleaner(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },
    get content() { return content; },
    get undoRedo() { return undoRedo; },
    get style() { return style; },
    get image() { return image; },
    get anchor() { return anchor; },

    get iFrameDoc() { return iFrameDoc; },
    get $winContent() { return $winContent; },
    get htmlFragmentWin() { return htmlFragmentWin; },
    get htmlFragment() { return htmlFragment; },
    set htmlFragment(v) { htmlFragment = v; },
    get inContainer() { return inContainer; },

    get $Style() { return $Style; },
    get waContentMarkerCounter() { return waContentMarkerCounter; },
    set waContentMarkerCounter(v) { waContentMarkerCounter = v; }


  });



  const tidy = new Tidy(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get extParameters() { return extParameters; }
  });



  const undoRedo = new UndoRedo(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },
    get contentChecker() { return contentChecker; },
    get copyPaste() { return copyPaste; },
    get anchor() { return anchor; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },

    setWaContentMarker,
    checkAllImages: function () { image.objectFactory.checkAllImages(); }
  });



  const style = new Style(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },
    pParent,

    get helpers() { return helpers; },
    get range() { return range; },
    get content() { return content; },
    get contentChecker() { return contentChecker; },
    get contentCleaner() { return contentCleaner; },
    get undoRedo() { return undoRedo; },
    get font() { return font; },
    get html() { return html; },
    get layer() { return layer; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get objIFrame() { return objIFrame; },
    get pDropdown() { return pDropdown; },
    set pDropdown(v) { pDropdown = v; },

    get isDropdownOpen() { return isDropdownOpen; },
    get checkAllBlockTags() { return checkAllBlockTags; },
    get checkAllBlockTagsExt() { return checkAllBlockTagsExt; },
    get checkAllBlockTagsBoth() { return checkAllBlockTagsBoth; },
    get pToolbarConfig() { return pToolbarConfig; },
    get $Style() { return $Style; },
    get htmlFragment() { return htmlFragment; },
    get waContentMarkerCounter() { return waContentMarkerCounter; },
    set waContentMarkerCounter(v) { waContentMarkerCounter = v; },

    openDropdown,
    closeDropdown,
    queryBasicCommandEnabled,
    queryBasicCommandState,
    execBasicCommand


  });



  const font = new Font(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },
    get style() { return style; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get pDropdown() { return pDropdown; },
    set pDropdown(v) { pDropdown = v; },

    get bodyStyles() { return bodyStyles; },
    get isDropdownOpen() { return isDropdownOpen; },

    openDropdown,
    closeDropdown
  });



  const copyPaste = new CopyPaste(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },
    get contentChecker() { return contentChecker; },
    get contentCleaner() { return contentCleaner; },
    get undoRedo() { return undoRedo; },
    get style() { return style; },
    get table() { return table; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get editorId() { return editorId; },

    get waContentMarkerCounter() { return waContentMarkerCounter; },
    set waContentMarkerCounter(v) { waContentMarkerCounter = v; },
    get $Style() { return $Style; },

    setWaContentMarker,
    onlykeyCutCopyPaste


  });



  const image = new Image(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get frameSize() { return frameSize; },
    get range() { return range; },
    get contentChecker() { return contentChecker; },
    get contentCleaner() { return contentCleaner; },
    get undoRedo() { return undoRedo; },
    get link() { return link; },
    get layer() { return layer; },
    get artText() { return artText; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get cutBox() { return cutBox; },
    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },
    get isEditorInHeader() { return isEditorInHeader; },
    get absElementsFixer() { return layer.objectFactory; },
    afterOpenDialog,
    closeDialog,
    afterCloseDialog,

    get queryStatePositionAbsolute() { return layer.queryStatePositionAbsolute; },

    get $winContent() { return $winContent; },
    get objIFrame() { return objIFrame; },
    get editorId() { return editorId; },
    get pToolbarConfig() { return pToolbarConfig; },
    get isNewFullScroll() { return isNewFullScroll; },
    get scrollableParents() { return scrollableParents; },
    get absImageSelected() { return absImageSelected; },
    set absImageSelected(v) { absImageSelected = v; },
    setEnabledCommand,

    get isAbsElementMoving() { return isAbsElementMoving; },
    fireEvent
  });



  const docum = new Document(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },
    get undoRedo() { return undoRedo; },
    get image() { return image; },
    get link() { return link; },

    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },

    get checkAllBlockTagsPlusA() { return checkAllBlockTagsPlusA; },
    get checkAllBlockTags() { return checkAllBlockTags; },

    afterOpenDialog,
    closeDialog,
    afterCloseDialog


  });



  const link = new Link(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },
    get pToolbarConfig() { return pToolbarConfig },
    get extParameters() { return extParameters },
    get pParent() { return pParent },

    get helpers() { return helpers; },
    get range() { return range; },
    get contentCleaner() { return contentCleaner; },
    get undoRedo() { return undoRedo; },
    get image() { return image; },
    get docum() { return docum; },
    get anchor() { return anchor; },
    get layer() { return layer; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get pDropdown() { return pDropdown; },
    set pDropdown(v) { pDropdown = v; },
    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },

    get isDropdownOpen() { return isDropdownOpen; },
    get checkAllBlockTags() { return checkAllBlockTags; },
    get checkAllBlockTagsPlusA() { return checkAllBlockTagsPlusA; },
    get isEditorInHeader() { return isEditorInHeader; },

    queryBasicCommandEnabled,
    execBasicCommand,
    openDropdown,
    closeDropdown,
    afterOpenDialog,
    closeDialog,
    afterCloseDialog
  });



  const anchor = new Anchor(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },
    get undoRedo() { return undoRedo; },
    get link() { return link; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },

    get isEditorInHeader() { return isEditorInHeader; },
    get checkAllBlockTags() { return checkAllBlockTags; },

    queryBasicCommandEnabled,
    afterOpenDialog,
    closeDialog,
    afterCloseDialog
  });



  const table = new Table(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },
    get undoRedo() { return undoRedo },

    get helpers() { return helpers; },
    get range() { return range; },
    get contentChecker() { return contentChecker; },

    get iFrameDoc() { return iFrameDoc; },
    get pDropdown() { return pDropdown; },
    set pDropdown(v) { pDropdown = v; },
    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },

    queryBasicCommandEnabled,
    openDropdown,
    closeDropdown,
    afterOpenDialog,
    closeDialog,
    afterCloseDialog
  });



  const layer = new Layer(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get frameSize() { return frameSize; },
    get mouse() { return mouse; },
    get range() { return range; },
    get contentChecker() { return contentChecker; },
    get undoRedo() { return undoRedo; },
    get image() { return image; },

    get iFrame() { return iFrame; },
    get iFrameDoc() { return iFrameDoc; },
    get cutBox() { return cutBox; },
    get pDropdown() { return pDropdown; },
    set pDropdown(v) { pDropdown = v; },

    get $Style() { return $Style; },
    get $StyleStrictInt() { return $StyleStrictInt; },

    openDropdown,
    closeDropdown,

    get $winContent() { return $winContent; },
    get objIFrame() { return objIFrame; },
    get editorId() { return editorId; },
    get isNewFullScroll() { return isNewFullScroll; },
    get scrollableParents() { return scrollableParents; },
    get isFocused() { return isFocused; },
    get isAbsElementMoving() { return isAbsElementMoving; },
    set isAbsElementMoving(v) { isAbsElementMoving = v; },
    get absImageSelected() { return absImageSelected; },
    set absImageSelected(v) { absImageSelected = v; },
    get isEditorNoScroll() { return isEditorNoScroll; },

    setEnabledCommand,

    findParentAbsElement,

    get checkDivImgTags() { return checkDivImgTags; }
  });



  const artText = new ArtText(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get extParameters() { return extParameters; },

    get helpers() { return helpers; },
    get range() { return range; },
    get contentChecker() { return contentChecker; },
    get contentCleaner() { return contentCleaner; },
    get undoRedo() { return undoRedo; },
    get image() { return image; },
    get layer() { return layer; },

    get iFrameDoc() { return iFrameDoc; },
    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },

    get isEditorInHeader() { return isEditorInHeader; },
    get $Style() { return $Style; },

    afterOpenDialog,
    closeDialog,
    afterCloseDialog
  });



  const divider = new Divider(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },

    get iFrameDoc() { return iFrameDoc; },

    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },

    afterOpenDialog,
    closeDialog,
    afterCloseDialog
  });



  const html = new HTML(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },
    get content() { return content; },
    get contentChecker() { return contentChecker; },
    get contentCleaner() { return contentCleaner; },
    get style() { return style; },
    get image() { return image; },
    get anchor() { return anchor; },

    get iFrameDoc() { return iFrameDoc; },
    get iFrameId() { return iFrameId; },

    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },

    get repairAndCleanHtml() { return repairAndCleanHtml; },
    get absImageSelected() { return absImageSelected; },
    set absImageSelected(v) { absImageSelected = v; },
    get isTidyServiceOn() { return isTidyServiceOn; },
    get $winContent() { return $winContent; },

    afterOpenDialog,
    closeDialog,
    afterCloseDialog,
    removeWaContentMarker,
    setWaContentMarker,
    queryBasicCommandEnabled
  });



  const emailMacros = new EmailMacros(
  {
    get pThis() { return pThis },
    get pToolbar() { return pToolbar },

    get helpers() { return helpers; },
    get range() { return range; },

    get extParameters() { return extParameters; },
    get pDialog() { return pDialog; },
    set pDialog(v) { pDialog = v; },

    afterOpenDialog,
    closeDialog,
    afterCloseDialog
  });



  let pEditor = pThis,
      topWin = BEditor.environment.topWindow,
      isIE = BEditor.browser.isIE,
      isGecko = BEditor.browser.isGecko,
      isWebKit = BEditor.browser.isWebKit,
      isTB = (!isIE || isIE > 9),
      $Style = function (element, att) { let eStyle = BEditor.styleHelpers.getElementStyle(element, att, iFrame); return ((eStyle != null) ? eStyle.toLowerCase() : ''); },
      $StyleStrictInt = function (element, att) { return BEditor.styleHelpers.getElementStyleStrictInt(element, att, iFrame); },
      $StyleInt = function (element, att) { return BEditor.styleHelpers.getElementStyleInt(element, att, iFrame); },
      saveAllDataAttempts,
      isLocalToolbar = !!extParameters.isLocalToolbar,
      placeHolder = extParameters.placeHolder,
      isGadget = false,
      isDropdownOpen = false,
      isMultiEditorMode = !!pParent.multiEditorMode,
      isNewFullScroll = ((window.EditorToolbarComposeEmailConfig && pToolbarConfig.name === EditorToolbarComposeEmailConfig.name) || (window.EditorToolbarCardsLocalConfig && pToolbarConfig.name === EditorToolbarCardsLocalConfig.name) || (window.EditorToolbarPollDescriptionLocalConfig && pToolbarConfig.name === EditorToolbarPollDescriptionLocalConfig.name));

  $winContent['thisEditorObjectStay___editorId_' + editorId] = pThis;

  let debugEditor = false,
      debugWinText = null,
      logCounter = 0,

      consoleLog = function (msg, isTrace) { return; if (debugEditor) { console.log(logCounter + ' => ' + msg); if (isTrace && !isIE) { console.trace(); } logCounter++; }};


  let $ = function(id) { return ((topWin.contentarea) ? topWin.contentarea.document.getElementById(id) : document.getElementById(id)) || $$('[data-componentId="' + id + '"]') || $$('[data-layoutid="' + id + '"]') || document.getElementById(id) || null; },
      $$ = function(query) { try { return ((topWin.contentarea) ? topWin.contentarea.document.querySelector(query) : document.querySelector(query)) || document.querySelector(query); } catch (e) { return null; } };

  let i;



  let parentContainer,
      cloneEditableArea,
      cutBox,
      cutBoxCup,
      inContainer,
      outContainer,
      previousInContainer,
      gadgetContainer = null,
      htmlElement,
      scrollableParents = [],
      objIFrame,
      iFrame,
      iFrameDoc,
      iFrameHtml,
      htmlFragmentElement,
      htmlFragmentWin,
      htmlFragmentHtml,
      htmlFragment,
      checkInsertedContainerTM = null,
      isFocused = false,
      isRangeKeyUp = false,
      isEditorInViewMode,
      pDialog,
      pDropdown,
      pParameters,
      isPersistEditing = false,
      isExternalPersistEditing = false,
      stopBluredEvent = false,
      isSetCursor,
      isEditEventDetails = false,
      waContentMarkerCounter = 1;

  let bodyStyles = {},
      currentBodyCSSClasses,
      bodyTransferStyles =
      {
        'background':
        {
          'backgroundColor': 'transparent',
          'backgroundImage': 'none',
          'backgroundRepeat': 'repeat',
          'backgroundPosition': '0 0',
          'backgroundSize': 'auto',
          'backgroundAttachment': 'scroll',
          'backgroundOrigin': 'padding-box',
          'backgroundClip': 'border-box'
        },

        'text':
        {
          'fontFamily': null,
          'fontSize': null,
          'color': null,
          'fontWeight': null,
          'fontStyle': null,
          'fontVariant': null,
          'fontStretch': null,
          'textDecoration': null,
          'letterSpacing': null,
          'lineHeight': null,
          'quotes': null,
          'textAlign': null,
          'textAlignLast': null,
          'textIndent': null,
          'textOverflow': null,
          'textShadow': null,
          'textTransform': null,
          'unicodeBidi': null,
          'whiteSpace': null,
          'wordBreak': null,
          'wordSpacing': null,
          'wordWrap': null,
          'writingMode': null,
          'verticalAlign': null
        }
      };

  let isEditorInHeader = false;
  let absImageSelected;

  let iFrameNameCounter = (new Date()).getTime();


  let cloneEditableAreaId = 'idEditorCloneEditableArea_' + iFrameNameCounter + '_' + editorId,
      cloneEditableAreaPrevId = cloneEditableAreaId,
      cutBoxId = 'idEditorCutBox_' + iFrameNameCounter + '_' + editorId,
      cutBoxPrevId = cutBoxId,
      iFrameId = 'idEditorIFrame_' + iFrameNameCounter + '_' + editorId,
      iFramePrevId = iFrameId,
      iFrameName = 'nmEditorIFrame_' + iFrameNameCounter + '_' + editorId,
      iFramePrevName = iFrameName;

  let htmlFragmentId = 'idEditorHTMLFragment_' + iFrameNameCounter + '_' + editorId;
  let htmlFragmentName = 'nmEditorHTMLFragment_' + iFrameNameCounter + '_' + editorId;
  let waitIFrameCounter;

  this.editorContentIFrameName = iFrameName;

  let editorMinHeight = null;

  if (minEditorHeight != null && !isNaN(parseInt(minEditorHeight)) && parseInt(minEditorHeight) >= 0)
  {
    editorMinHeight = parseInt(minEditorHeight);
  }

  let checkContentTM = null;

  let checkAllBlockTags = /^(body|p|h[1-6]|div|pre|li|blockquote|center|dt|dd|td|th|address|marquee)$/i,
      checkAllBlockTagsExt = /^(body|p|h[1-6]|div|pre|ul|ol|blockquote|center|dt|dd|table|address|marquee)$/i,
      checkAllBlockTagsBoth = /^(body|p|h[1-6]|div|pre|ul|ol|li|blockquote|center|dt|dd|table|td|th|address|marquee)$/i,
      checkAllBlockTagsPlusA = /^(body|p|h[1-6]|div|pre|li|blockquote|center|dt|dd|td|th|address|marquee|a)$/i,
      checkDivImgTags = /^(div|img)$/i,
      checkTableTag = /^table$/i,
      checkThTdTags = /^(th|td)$/i;





  let matchShowTablesBordersCookie;
  let isShowTablesBordersCookie = 1;
  let matchShowAnchorCookie;
  let isShowAnchorCookie = 1;
  let additionalBodyClassName;

  let isAbsElementMoving = false;

  let isTidyServiceOn = false;
  let repairAndCleanHtml = null;

  let iosCurrentImage = null,
      iosCurrentRange = null,
      iosOldRange = {},
      setIOSCheckRefreshStateTM = null;


  let setIsDropdownOpenTM = null;


  const hCommand =
        {
          undo: { handler: undoRedo.execUndoRedo, params: 'undo' },
          redo: { handler: undoRedo.execUndoRedo, params: 'redo' },
          cut: { handler: ((isIE) ? execBasicCommand : isWebKit ? copyPaste.webkit.webkitCutCopyPaste : onlykeyCutCopyPaste), params: 'cut' },
          copy: { handler: ((isIE) ? execBasicCommand : isWebKit ? copyPaste.webkit.webkitCutCopyPaste : onlykeyCutCopyPaste), params: 'copy' },
          paste: { handler: (isIE ? execBasicCommand : isWebKit ? copyPaste.webkit.webkitCutCopyPaste : onlykeyCutCopyPaste), params: 'paste' },
          bold: { handler: execBasicCommand, params: 'bold', args: { useBookmark: true }},
          italic: { handler: execBasicCommand, params: 'italic', args: { useBookmark: true }},
          underline: { handler: execBasicCommand, params: 'underline', args: { useBookmark: true }},
          strikethrough: { handler: execBasicCommand, params: 'strikethrough', args: { useBookmark: true }},
          superscript: { handler: execBasicCommand, params: 'superscript', args: { useBookmark: true }},
          subscript: { handler: execBasicCommand, params: 'subscript', args: { useBookmark: true }},
          openTextFontDropdown: { handler: font.family.openTextFontDropdown, params: null },
          setTextFont: { handler: font.family.setTextFont, params: null },
          openFontSizeDropdown: { handler: font.size.openFontSizeDropdown, params: null },
          setFontSize: { handler: font.size.setFontSize, params: null },
          openLineHeightDropdown: { handler: style.lineHeight.openLineHeightDropdown, params: null },
          setLineHeight: { handler: style.lineHeight.setLineHeight, params: null },
          openForeColorDropdown: { handler: font.color.openForeColorDropdown, params: null },
          setForeColor: { handler: font.color.setForeColor, params: null },
          openBackColorDropdown: { handler: font.background.openBackColorDropdown, params: null },
          setBackColor: { handler: font.background.setBackColor, params: null },
          copyStyle: { handler: style.formatPainter.copyStyle, params: null },
          insertHR: { handler: divider.inserter.openInsertDividerDialog, params: 'inserthorizontalrule' },
          insertOL: { handler: execBasicCommand, params: 'insertorderedlist', args: { useBookmark: true, beforeCallback: style.normalizer.fixWebKitSelfStyles, beforeArgs: [true, 'insertorderedlist'], afterCallback: () => { helpers.fixers.fixWebKitList(); style.normalizer.fixWebKitSelfStyles(false, 'insertorderedlist'); helpers.fixers.fixIEListDiv(); }, afterArgs: null }},
          insertUL: { handler: execBasicCommand, params: 'insertunorderedlist', args: { useBookmark: true, beforeCallback: style.normalizer.fixWebKitSelfStyles, beforeArgs: [true, 'insertunorderedlist'], afterCallback: () => { helpers.fixers.fixWebKitList(); style.normalizer.fixWebKitSelfStyles(false, 'insertunorderedlist'); helpers.fixers.fixIEListDiv(); }, afterArgs: null }},
          indentBlock: { handler: execBasicCommand, params: 'indent', args: { useBookmark: true, beforeCallback: style.normalizer.fixWebKitSelfStyles, beforeArgs: [true, 'indent'], afterCallback: style.normalizer.fixWebKitSelfStyles, afterArgs: [false, 'indent'] }},
          outdentBlock: { handler: execBasicCommand, params: 'outdent', args: { useBookmark: true }},
          justify: { handler: function() { }, params: null },
          justifyLeft: { handler: style.justifier.execJustify, params: 'justifyleft' },
          justifyCenter: { handler: style.justifier.execJustify, params: 'justifycenter' },
          justifyRight: { handler: style.justifier.execJustify, params: 'justifyright' },
          justifyFull: { handler: style.justifier.execJustify, params: 'justifyfull' },
          justifyBlock: { handler: function() { }, params: null },
          justifyTop: { handler: style.justifier.execJustify, params: 'justifytop' },
          justifyMiddle: { handler: style.justifier.execJustify, params: 'justifymiddle' },
          justifyBottom: { handler: style.justifier.execJustify, params: 'justifybottom' },
          openBlockFormattingDropdown: { handler: style.blockFormatter.openBlockFormattingDropdown, params: null },
          blockFormatting: { handler: style.blockFormatter.blockFormatting, params: null },
          openInsertImageDialog: { handler: image.inserter.openInsertImageDialog, params: null },
          insertImage: { handler: image.inserter.insertImage, params: null },
          showImageSettingsToolBar: { handler: image.ieditor.showImageSettingsToolBar, params: null },

          openInsertArtTextDialog: { handler: artText.inserter.openInsertArtTextDialog, params: null },
          insertArtText: { handler: artText.inserter.insertArtText, params: null },

          openLinkDropdown: { handler: link.openLinkDropdown, params: null },
          linkDropdownHandler: { handler: link.linkDropdownHandler, params: null },
          openInsertLinkDialog: { handler: link.inserter.openInsertLinkDialog, params: null },
          insertLink: { handler: link.inserter.insertLink, params: null },
          removeLink: { handler: link.remover.removeLink, params: '' },

          openInsertAnchorDialog: { handler: anchor.inserter.openInsertAnchorDialog, params: null },
          insertAnchor: { handler: anchor.inserter.insertAnchor, params: null },
          openInsertLinkToAnchorDialog: { handler: anchor.link.openInsertLinkToAnchorDialog, params: null },
          insertLinkToAnchor: { handler: link.inserter.insertLink, params: null },
          insertTableOfContent: { handler: anchor.toc.insertTableOfContent, params: null },
          setAnchorsVisibility: { handler: anchor.setAnchorsVisibility, params: null },

          openInsertDocumentDialog: { handler: docum.insert.openInsertDocumentDialog, params: null },
          openEditDocumentDialog: { handler: docum.insert.openInsertDocumentDialog, params: null },

          openTableDropdown: { handler: table.openTableDropdown, params: null },
          tableDropdownHandler: { handler: table.tableDropdownHandler, params: null },
          openInsertTableDialog: { handler: table.insert.openInsertTableDialog, params: null },
          insertTable: { handler: table.insert.insertTable, params: null },
          addTableRowAfter: { handler: table.edit.basicEditTable, params: { element: 'row', action: 'add', position: 'after'} },
          addTableRowBefore: { handler: table.edit.basicEditTable, params: { element: 'row', action: 'add', position: 'before'} },
          addTableColumnAfter: { handler: table.edit.basicEditTable, params: { element: 'column', action: 'add', position: 'after'} },
          addTableColumnBefore: { handler: table.edit.basicEditTable, params: { element: 'column', action: 'add', position: 'before'} },
          deleteTable: { handler: table.edit.basicEditTable, params: { element: 'table', action: 'delete', position: null} },
          deleteTableRow: { handler: table.edit.basicEditTable, params: { element: 'row', action: 'delete', position: null} },
          deleteTableColumn: { handler: table.edit.basicEditTable, params: { element: 'column', action: 'delete', position: null} },
          openTableMoreDialog: { handler: table.edit.openTableMoreDialog, params: null },
          tableMoreDialogHandler: { handler: table.edit.tableMoreDialogHandler, params: null },
          setTableBorderAndBackground: { handler: table.edit.setTableBorderAndBackground, params: null },
          setTableAlignment: { handler: table.edit.setTableAlignment, params: null },
          setTableSizeAndPadding: { handler: table.edit.setTableSizeAndPadding, params: null },
          setTableCellsPadding: { handler: table.edit.openTableMoreDialog, params: { 'command': 'setTableCellsPadding', 'title': 'Padding' } },
          showHideTablesBorders: { handler: table.edit.showHideTablesBorders, params: null },

          openLayerDropdown: { handler: layer.openLayerDropdown, params: null },
          layerDropdownHandler: { handler: layer.layerDropdownHandler, params: null },
          setPositionAbsolute: { handler: layer.setPositionAbsolute, params: null },
          setZIndexForward: { handler: layer.setZIndex, params: 'Forward' },
          setZIndexBack: { handler: layer.setZIndex, params: 'Back' },
          horizontalAlignLeftLayer: { handler: layer.alignLayer, params: 'Left' },
          horizontalAlignCenterLayer: { handler: layer.alignLayer, params: 'Center' },
          horizontalAlignRightLayer: { handler: layer.alignLayer, params: 'Right' },
          verticalAlignTopLayer: { handler: layer.alignLayer, params: 'Top' },
          verticalAlignMiddleLayer: { handler: layer.alignLayer, params: 'Middle' },
          verticalAlignBottomLayer: { handler: layer.alignLayer, params: 'Bottom' },

          openInsertSnippetDialog: { handler: html.snippet.openInsertSnippetDialog, params: null },
          insertSnippet: { handler: html.snippet.insertSnippet, params: null },
          openEditHTMLDialog: { handler: html.editor.openEditHTMLDialog, params: null },
          cleanupHTML: { handler: style.cleaner.cleanupHTML, params: null },
          replaceAllHTML: { handler: html.editor.replaceAllHTML, params: '' },

          openMoreLocalDropdown: { handler: openMoreLocalDropdown, params: null },
          moreLocalHandler: { handler: moreLocalHandler, params: null },

          openInsertEmailMacrosDialog: { handler: emailMacros.openInsertEmailMacrosDialog, params: null },
          insertEmailMacros: { handler: emailMacros.insertEmailMacros, params: null }
        },

        hEnabledCommand =
        {
          undo:                           { enabled: true, handlers: [{ handler: undoRedo.queryExecUndoRedo, params: 'undo' }]},
          redo:                           { enabled: true, handlers: [{ handler: undoRedo.queryExecUndoRedo, params: 'redo' }]},
          cut:                            { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'cut' }]},
          copy:                           { enabled: true, handlers: [{ handler: ((!isWebKit) ? queryBasicCommandEnabled : function() { return true; }), params: 'copy' }]},
          paste:                          { enabled: true, handlers: [{ handler: ((!isWebKit && false) ? queryBasicCommandEnabled : function() { return true; }), params: 'paste' }]},
          bold:                           { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'bold' }]},
          italic:                         { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'italic' }]},
          underline:                      { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'underline' }]},
          strikethrough:                  { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'strikethrough' }]},
          superscript:                    { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'superscript' }]},
          subscript:                      { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'subscript' }]},
          openTextFontDropdown:           { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'fontname' }]},
          openFontSizeDropdown:           { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'fontsize' }]},
          openLineHeightDropdown:         { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'formatblock' }]},
          openForeColorDropdown:          { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'forecolor' }]},
          openBackColorDropdown:          { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'backcolor' }]},
          copyStyle:                      { enabled: true, handlers: [{ handler: style.formatPainter.queryCopyStyleEnabled, params: '' }]},
          insertHR:                       { enabled: true, handlers: [{ handler: function () { return true; }, params: 'inserthorizontalrule' }]},
          insertOL:                       { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'insertorderedlist' }]},
          insertUL:                       { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'insertunorderedlist' }]},
          indentBlock:                    { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'indent' }]},
          outdentBlock:                   { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'outdent' }]},
          justify:                        { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justify' }]},
          justifyLeft:                    { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justifyleft' }]},
          justifyCenter:                  { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justifycenter' }]},
          justifyRight:                   { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justifyright' }]},
          justifyFull:                    { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justifyfull' }]},
          justifyBlock:                   { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justifyblock' }]},
          justifyTop:                     { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justifytop' }]},
          justifyMiddle:                  { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justifymiddle' }]},
          justifyBottom:                  { enabled: true, handlers: [{ handler: style.justifier.queryJustifyEnabled, params: 'justifybottom' }]},
          openBlockFormattingDropdown:    { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'formatblock' }]},
          openInsertImageDialog:          { enabled: true, handlers: [{ handler: queryBasicCommandEnabled, params: 'insertimage' }]},

          openInsertArtTextDialog:        { enabled: true, handlers: [{ handler: artText.queryArtTextEnabled, params: '' }]},

          openLinkDropdown:               { enabled: true },
          openInsertLinkDialog:           { enabled: true, handlers: [{ handler: link.inserter.queryOpenInsertLinkDialogEnabled, params: 'openInsertLinkDialog' }]},
          removeLink:                     { enabled: true, handlers: [{ handler: link.remover.queryRemoveLinkEnabled, params: '' }]},

          openInsertAnchorDialog:         { enabled: true, handlers: [{ handler: anchor.inserter.queryOpenInsertAnchorDialogEnabled, params: 'openInsertAnchorDialog' }]},
          openInsertLinkToAnchorDialog:   { enabled: true, handlers: [{ handler: anchor.link.queryOpenInsertLinkToAnchorDialogEnabled, params: 'openInsertLinkToAnchorDialog' }]},
          insertTableOfContent:           { enabled: true, handlers: [{ handler: anchor.toc.queryInsertTableOfContent, params: 'insertTableOfContent' }]},
          setAnchorsVisibility:           { enabled: true },

          openInsertDocumentDialog:       { enabled: true, handlers: [{ handler: docum.insert.queryOpenInsertDocumentDialogEnabled, params: 'openInsertDocumentDialog' }]},
          openEditDocumentDialog:         { enabled: true, handlers: [{ handler: docum.edit.queryOpenEditDocumentDialogEnabled, params: 'openInsertDocumentDialog' }]},

          openTableDropdown:              { enabled: true },
          openInsertTableDialog:          { enabled: true, handlers: [{ handler: table.insert.queryOpenInsertTableDialogEnabled, params: 'openInsertTableDialog' }]},
          setTableCellsPadding:           { enabled: false},
          addTableRowAfter:               { enabled: true, handlers: [{ handler: table.edit.queryBasicEditTable, params: { element: 'row', action: 'add', position: 'after'} }]},
          addTableRowBefore:              { enabled: true, handlers: [{ handler: table.edit.queryBasicEditTable, params: { element: 'row', action: 'add', position: 'before'} }]},
          addTableColumnAfter:            { enabled: true, handlers: [{ handler: table.edit.queryBasicEditTable, params: { element: 'column', action: 'add', position: 'after'} }]},
          addTableColumnBefore:           { enabled: true, handlers: [{ handler: table.edit.queryBasicEditTable, params: { element: 'column', action: 'add', position: 'before'} }]},
          deleteTableRow:                 { enabled: true, handlers: [{ handler: table.edit.queryBasicEditTable, params: { element: 'row', action: 'delete', position: null} }]},
          deleteTableColumn:              { enabled: true, handlers: [{ handler: table.edit.queryBasicEditTable, params: { element: 'column', action: 'delete', position: null} }]},
          showHideTablesBorders:          { enabled: true },

          openLayerDropdown:              { enabled: true },
          setPositionAbsolute:            { enabled: true },
          setZIndexForward:               { enabled: true },
          setZIndexBack:                  { enabled: true },
          horizontalAlignLeftLayer:       { enabled: true },
          horizontalAlignCenterLayer:     { enabled: true },
          horizontalAlignRightLayer:      { enabled: true },
          verticalAlignTopLayer:          { enabled: true },
          verticalAlignMiddleLayer:       { enabled: true },
          verticalAlignBottomLayer:       { enabled: true },


          openInsertSnippetDialog:        { enabled: true, handlers: [{ handler: html.snippet.queryOpenInsertSnippetDialogEnabled, params: 'inserthtml' }]},
          cleanupHTML:                    { enabled: true },
          openEditHTMLDialog:             { enabled: !(extParameters.disableJsEditing == true) },

          openMoreLocalDropdown:          { enabled: true },

          openInsertEmailMacrosDialog:    { enabled: true, handlers: [{ handler: emailMacros.queryOpenInsertEmailMacrosDialog, params: null }]}
        },

        hStateCommand =
        {
          undo: { handler: function() { return false; }, params: 'undo' },
          redo: { handler: function() { return false; }, params: 'redo' },
          cut: { handler: function() { return false; }, params: 'cut' },
          copy: { handler: function() { return false; }, params: 'copy' },
          paste: { handler: function() { return false; }, params: 'paste' },
          bold: { handler: queryBasicCommandState, params: 'bold' },
          italic: { handler: queryBasicCommandState, params: 'italic' },
          underline: { handler: queryBasicCommandState, params: 'underline' },
          strikethrough: { handler: queryBasicCommandState, params: 'strikethrough' },
          superscript: { handler: queryBasicCommandState, params: 'superscript' },
          subscript: { handler: queryBasicCommandState, params: 'subscript' },
          openTextFontDropdown: { handler: function() { return false; }, params: null },
          openFontSizeDropdown: { handler: function() { return false; }, params: null },
          openLineHeightDropdown: { handler: function() { return false; }, params: null },
          openForeColorDropdown: { handler: function() { return false; }, params: null },
          openBackColorDropdown: { handler: function() { return false; }, params: null },
          copyStyle: { handler: style.formatPainter.queryCopyStyleState, params: null },
          insertHR: { handler: function () { return false; }, params: 'inserthorizontalrule' },
          insertOL: { handler: queryBasicCommandState, params: 'insertorderedlist' },
          insertUL: { handler: queryBasicCommandState, params: 'insertunorderedlist' },
          indentBlock: { handler: ((isIE) ? queryBasicCommandState : null), params: 'indent' },
          outdentBlock: { handler: ((isIE) ? queryBasicCommandState : null), params: 'outdent' },
          justify: { handler: function() { return false; }, params: null },
          justifyLeft: { handler: style.justifier.queryJustifyState, params: 'justifyleft' },
          justifyCenter: { handler: style.justifier.queryJustifyState, params: 'justifycenter' },
          justifyRight: { handler: style.justifier.queryJustifyState, params: 'justifyright' },
          justifyFull: { handler: style.justifier.queryJustifyState, params: 'justifyfull' },
          justifyBlock: { handler: function() { return false; }, params: null },
          justifyTop: { handler: style.justifier.queryJustifyState, params: 'justifytop' },
          justifyMiddle: { handler: style.justifier.queryJustifyState, params: 'justifymiddle' },
          justifyBottom: { handler: style.justifier.queryJustifyState, params: 'justifybottom' },
          openBlockFormattingDropdown: { handler: function() { return false; }, params: null },
          openInsertImageDialog: { handler: image.inserter.queryInsertImageState, params: '' },

          openInsertArtTextDialog: { handler: artText.queryInsertArtTextState, params: '' },

          openLinkDropdown: { handler: function() { return false; }, params: null },
          openInsertLinkDialog: { handler: link.remover.queryRemoveLinkEnabled, params: '' },
          removeLink: { handler: queryBasicCommandState, params: 'unlink' },

          openInsertAnchorDialog: { handler: function() { return false; }, params: '' },
          openInsertLinkToAnchorDialog: { handler: function() { return false; }, params: '' },
          insertTableOfContent: { handler: function() { return false; }, params: '' },
          setAnchorsVisibility: { handler: anchor.queryStateAnchors, params: '' },

          openInsertDocumentDialog: { handler: docum.insert.queryInsertDocumentState, params: '' },
          openEditDocumentDialog: { handler: function() { return false; }, params: '' },

          openTableDropdown: { handler: function() { return false; }, params: '' },
          openInsertTableDialog: { handler: table.queryInsertTableState, params: '' },
          addTableRowAfter: { handler: function() { return false; }, params: '' },
          addTableRowBefore: { handler: function() { return false; }, params: '' },
          addTableColumnAfter: { handler: function() { return false; }, params: '' },
          addTableColumnBefore: { handler: function() { return false; }, params: '' },
          deleteTableRow: { handler: function() { return false; }, params: '' },
          deleteTableColumn: { handler: function() { return false; }, params: '' },
          showHideTablesBorders: { handler: table.edit.queryStateTablesBorders, params: null },

          openLayerDropdown: { handler: function() { return false; }, params: null },
          setPositionAbsolute: { handler: function() { return false; }, params: null },
          setZIndexForward: { handler: function() { return false; }, params: null },
          setZIndexBack: { handler: function() { return false; }, params: null },
          horizontalAlignLeftLayer: { handler: function() { return false; }, params: null },
          horizontalAlignCenterLayer: { handler: function() { return false; }, params: null },
          horizontalAlignRightLayer: { handler: function() { return false; }, params: null },
          verticalAlignTopLayer: { handler: function() { return false; }, params: null },
          verticalAlignMiddleLayer: { handler: function() { return false; }, params: null },
          verticalAlignBottomLayer: { handler: function() { return false; }, params: null },

          openInsertSnippetDialog: { handler: html.snippet.queryInsertSnippetState, params: '' },
          cleanupHTML: { handler: function() { return false; }, params: null },
          openEditHTMLDialog: { handler: function() { return false; }, params: null },

          openMoreLocalDropdown: { handler: function() { return false; }, params: null },

          openInsertEmailMacrosDialog: { handler: function() { return false; }, params: '' }
        };

  const extRefreshStateButtons =
        [
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'superscript',
          'subscript',
          'openBlockFormattingDropdown',
          'openTextFontDropdown',
          'openFontSizeDropdown',
          'openLineHeightDropdown'
        ];

  this.initEditor = initEditor;
  this.setReloadUrlParameter = setReloadUrlParameter;
  this.insertHTML = (...args) => helpers.insertHTML(...args);;
  this.restoreLinksToAnchors = (...args) => anchor.restoreLinksToAnchors(...args);
  this.mouseDragDrop = (...args) => mouse.mouseDragDropDo(...args);
  this.getAnchorsList = anchor.getAnchorsListDo;
  this.setAnchorsVisibility = anchor.setAnchorsVisibilityDo;
  this.editorUnreged = editorUnreged;

  BEditor.EditorsManager.setIsEditorsInViewMode((displayMode && displayMode === 'view'));

  let waitScore;
  let setObjValidTM = null;
  let setObjStayTM = setTimeout(function () { pThis.isObjStay = true; }, 1500);
  let isOnlyFirstAttemptInit = false;


  function initEditor (viewEditMode)
  {
    BEditor.Config.Ready.addHandler(function() { setTimeout(function () { initEditorDo(viewEditMode); }, 17); });
  }


  function initEditorDo (viewEditMode)
  {
    pToolbarConfig = (typeof(pToolbarConfig) === 'string') ? window[pToolbarConfig] : pToolbarConfig;

    if (pToolbar)
    {
      pToolbar.editorStarting(pThis);
    }

    if (pThis.isObjStay && (!$winContent['thisEditorObjectStay___editorId_' + editorId] || $winContent['thisEditorObjectStay___editorId_' + editorId] !== pThis))
    {
      if (setObjStayTM) clearTimeout(setObjStayTM);
      pThis.isObjStay = false;
      pThis.isObjValid = 2;
      return false;
    }

    const isEditorInViewModeNew = (viewEditMode && typeof (viewEditMode) === 'string' && viewEditMode.toLowerCase() === 'view');

    if (pThis.isObjValid && isEditorInViewMode === isEditorInViewModeNew)
    {
      return true;
    }

    if (pThis.isObjValid === 2)
    {
      isOnlyFirstAttemptInit = true;
    }

    if (setObjStayTM) clearTimeout(setObjStayTM);
    pThis.isObjStay = false;
    saveAllDataAttempts = 7;
    isEditorInViewMode = isEditorInViewModeNew;
    BEditor.EditorsManager.setIsEditorsInViewMode(isEditorInViewMode);

    if (!isEditorInViewMode && extParameters.ExpandButtons)
    {
      BEditor.ExtensionsManager.initButtonsExtensions(extParameters);
    }

    isTidyServiceOn = false;
    repairAndCleanHtml = function (contentHTML, callBackFunction) { if (callBackFunction) callBackFunction(contentHTML); };

    //TODO:Dime ON/OFF Tidy !!!
    if(extParameters.isTidyServiceOn && !debugEditor)
    {
      isTidyServiceOn = true;
      repairAndCleanHtml = tidy.requestTidyService;
    }

    setTimeout(initEditorStep2, (isTB ? 17 : 500));
  }


  function initEditorStep2 ()
  {
    let editableArea;

    inContainer = $(idIn);
    outContainer = idOut ? $(idOut) : false;
    parentContainer = (inContainer) ? inContainer.parentNode : null;

    if (inContainer && (outContainer || outContainer === false) && parentContainer)
    {
      content.isInTextArea = (inContainer.nodeName.toUpperCase() === 'TEXTAREA');

      if (!content.isInTextArea)
      {
        isGadget = false;
        editableArea = inContainer.querySelector('[data-editableArea]');

        if (editableArea)
        {
          gadgetContainer = inContainer;
          inContainer = editableArea;
          parentContainer = inContainer.parentNode;

          if (!editableArea.id)
          {
            editableArea.id = idIn + '_editableArea_waEditorTmpId';
          }

          idIn = editableArea.id;

          isGadget = true;
        }

        frameSize.setEditorFixedHeight(editableArea || inContainer);
        frameSize.setEditorNoScroll(editableArea || inContainer);
      }

      waitScore = extParameters.waitScore || 373 + BEditor.EditorsManager.setWaitScore(inContainer);

      if (BEditor.EditorsManager.getBrowserIsValid())
      {
        cloneEditableArea = $(cloneEditableAreaPrevId);
        cutBox = $(cutBoxPrevId);
        cutBoxCup = $(cutBoxPrevId + '_Cup');
        objIFrame = $(iFramePrevId);
        iFrame = (objIFrame ? objIFrame.contentWindow : null);
        helpers.brokenIFrame.testBrokenIFrame();
        htmlFragmentWin = (htmlFragmentElement ? htmlFragmentElement.contentWindow : null);
        iFrame = (objIFrame ? objIFrame.contentWindow : null);

        if (cutBox && cutBox.parentNode && (isIE || (cutBoxCup && cutBoxCup.parentNode)) && objIFrame && objIFrame.parentNode && (!isGadget || cloneEditableArea) && iFrame && iFrame.document && iFrame.document.body && htmlFragmentWin && htmlFragmentWin.document && htmlFragmentWin.document.body)
        {
          cloneEditableAreaId = cloneEditableAreaPrevId;
          cutBoxId = cutBoxPrevId;
          iFrameId = iFramePrevId;
          iFrameName = iFramePrevName;

          cutBox.style.display = 'none';

          if (previousInContainer)
          {
            previousInContainer.style.display = 'block';
          }

          iFrame.document.body.innerHTML = '##WA###BlankHTMLPageForEditorContentNew###WA##';
          htmlFragmentWin.document.body.innerHTML = '##WA###BlankHTMLPageForEditorContentNew###WA##';
        }
        else
        {
          if (isGadget)
          {
            cloneEditableArea = $winContent.document.createElement('DIV');
            cloneEditableArea.id = cloneEditableAreaId;
            cloneEditableArea.setAttribute('class', (editableArea.getAttribute('class') || ''));
            cloneEditableArea.setAttribute('style', (editableArea.getAttribute('style') || ''));
            cloneEditableArea.style.display = 'none';
          }

          cutBox = $winContent.document.createElement('DIV');
          cutBox.id = cutBoxId;
          cutBox.style.position = 'relative';
          cutBox.style.height = 'auto';
          cutBox.style.overflow = 'hidden';
          cutBox.style.display = 'none';

          cutBox.innerHTML = '<iframe src="' + blankPageURL + '" id="' + iFrameId + '" name="' + iFrameName + '" frameborder="0" scrolling="' + ((isGecko) ? 'no' : 'yes') + '" hspace="0" vspace="0" marginwidth="0" marginheight="0" allowtransparency="true" style="width: 1px; height: 1px; ' + ((isIE) ? '' : 'overflow-x: auto; overflow-y: hidden; background: none; background-color: transparent;') + '"></iframe>' + ((!isIE) ? '<div id="' + cutBoxId + '_Cup" style="position: absolute; left: 0; bottom: 0; width: 100%; height: 0; z-index: 7; overflow: hidden; background-color: transparent; background: none; cursor: text;"></div>' : '');

          if (isGadget)
          {
            cloneEditableArea.appendChild(cutBox);
            parentContainer.insertBefore(cloneEditableArea, inContainer);
          }
          else
          {
            parentContainer.insertBefore(cutBox, inContainer);
          }

          if (!htmlFragmentElement || !BEditor.environment.topWindow.frames[htmlFragmentName])
          {
            htmlFragmentElement = BEditor.environment.topWindow.document.createElement('IFRAME');
            htmlFragmentElement.id = htmlFragmentId;
            htmlFragmentElement.name = htmlFragmentName;
            htmlFragmentElement.setAttribute('style', 'position: absolute; left: -3700px; top: -3700px; width: 100px; height: 100px;');
            BEditor.environment.topWindow.document.body.appendChild(htmlFragmentElement);
          }

          htmlFragmentElement.src = blankPageURL;

          cloneEditableArea = $(cloneEditableAreaId);
          cutBox = $(cutBoxId);
          cutBoxCup = $(cutBoxId + '_Cup');
          objIFrame = $(iFrameId);
          iFrame = (objIFrame ? objIFrame.contentWindow : null);
          helpers.brokenIFrame.testBrokenIFrame();
          htmlFragmentWin = (htmlFragmentElement ? htmlFragmentElement.contentWindow : null);
        }

        inContainer = $(idIn);
        waitIFrameCounter = 0;

        if (inContainer && cutBox && objIFrame && iFrame && htmlFragmentElement && htmlFragmentWin)
        {
          pThis.isObjValid = 1;
          fireEvent('inited');
        }

        if (cutBoxCup)
        {
          cutBoxCup.onmousedown = persistEditing;
          cutBoxCup.onmouseup = resetPersistEditing;
        }

        if (autoStart)
        {
          if (isEditorInViewMode)
          {
            setTimeout(startViewing, (isTB ? 17 : 500));
          }
          else
          {
            setTimeout(pThis.startEditing, (isTB ? 17 : 500));
          }
        }

        if (BEditor.AdminPanel && BEditor.AdminPanel.ContentAreaUnload) //TODO:Dime
        {
          BEditor.AdminPanel.ContentAreaUnload.addHandler(disposeEditor);
        }
      }
      else
      {
        inContainer.style.display = 'block';

        if (inContainer)
        {
          pThis.isObjValid = 2;
        }
      }
    }
    else if (!isOnlyFirstAttemptInit)
    {
      setTimeout(initEditorStep2, 100);
    }
  }


  function setReloadUrlParameter (reloadUrlParameter)
  {
    pThis.reloadUrlParameter = reloadUrlParameter;
  }



  this.reInitEditor = function(dEditorId, dIdIn, dIdOut, dBlankPageURL, dDisplayMode, dPToolbar, dPToolbarConfig, dAutoStart, dAutoFocus, dMinEditorHeight, mainIdForBodyStyles, mainClassForBodyStyles, dExtParameters)
  {
    pThis.isObjExist = true;
    pThis.isObjValid = false;
    pThis.isObjStay = false;
    pThis.isObjDeleted = false;
    $winContent = (topWin.contentarea && !(isAdminComponent || isReactComponentActive)) ? topWin.contentarea : window;
    $winContent['thisEditorObjectStay___editorId_' + editorId] = this;

    let i;

    saveAllDataAttempts = 7;

    editorId = dEditorId;
    idIn = dIdIn;
    idOut = dIdOut;
    blankPageURL = dBlankPageURL + BEditor.themeResourcesPostfix;
    pToolbar = (dPToolbar) ? dPToolbar : null;
    pToolbarConfig = dPToolbarConfig;
    autoStart = (dAutoStart) ? dAutoStart : false;
    autoFocus = (dAutoFocus) ? dAutoFocus : false;
    editorMinHeight = null;
    displayMode = dDisplayMode;
    BEditor.EditorsManager.setIsEditorsInViewMode((displayMode && displayMode === 'view'));
    extParameters = dExtParameters;
    isTidyServiceOn = false;
    repairAndCleanHtml = null;
    isEditEventDetails = false;

    isLocalToolbar = !!(extParameters.isLocalToolbar);
    placeHolder = extParameters.placeHolder;

    helpers.reInit();
    key.reInit();
    range.reInit();
    undoRedo.reInit();
    style.reInit();

    image.currentImage = null;
    image.execCurrentImage = null;
    image.saveCurrentImage = null;
    waContentMarkerCounter = 2;

    image.reInit();
    scrollableParents.splice(0, scrollableParents.length);

    if (dMinEditorHeight != null && !isNaN(parseInt(dMinEditorHeight)) && parseInt(dMinEditorHeight) >= 0)
    {
      editorMinHeight = parseInt(dMinEditorHeight);
    }

    frameSize.reInit();
    range.stopCheckSelection();

    if (checkInsertedContainerTM) clearInterval(checkInsertedContainerTM);
    checkInsertedContainerTM = null;
    if (setIOSCheckRefreshStateTM) clearInterval(setIOSCheckRefreshStateTM);
    setIOSCheckRefreshStateTM = null;

    if (checkContentTM)
    {
      clearInterval(checkContentTM);
      checkContentTM = null;
    }

    previousInContainer = inContainer;
    gadgetContainer = null;

    iFrameNameCounter = (new Date()).getTime();
    cloneEditableAreaPrevId = cloneEditableAreaId;
    cloneEditableAreaId = 'idEditorCloneEditableArea_' + iFrameNameCounter + '_' + editorId;
    cutBoxPrevId = cutBoxId;
    cutBoxId = 'idEditorCutBox_' + iFrameNameCounter + '_' + editorId;
    iFramePrevId = iFrameId;
    iFrameId = 'idEditorIFrame_' + iFrameNameCounter + '_' + editorId;
    iFramePrevName = iFrameName;
    iFrameName = 'nmEditorIFrame_' + iFrameNameCounter + '_' + editorId;

    this.editorContentIFrameName = iFrameName;

    setObjStayTM = setTimeout(function () { pThis.isObjStay = true; }, 737);
  };


  this.dispose = function (dEditorId, dIdIn, dIdOut, dBlankPageURL, dPToolbar, dAutoStart, dAutoFocus, dMinEditorHeight)
  {
    pThis.isObjValid = false;

    frameSize.dispose();
    range.stopCheckSelection();

    if (checkInsertedContainerTM) clearInterval(checkInsertedContainerTM);
    if (setIOSCheckRefreshStateTM) clearInterval(setIOSCheckRefreshStateTM);

    if (checkContentTM)
    {
      clearInterval(checkContentTM);
      checkContentTM = null;
    }
  };


  function startViewing ()
  {
    if (!pThis.isObjValid)
    {
      return false;
    }

    try
    {
      iFrameDoc = iFrame.document;
    }
    catch (err)
    {
      if (isGecko)
      {
        setTimeout(fixBrokenIFrameForced, 3700 * helpers.brokenIFrame.counter);
        setTimeout(startViewing, 3700 * helpers.brokenIFrame.counter + 250 * helpers.brokenIFrame.counter);
        return false;
      }
      else
      {
        setTimeout(startViewing, 100);
        return false;
      }
    }


    if (objIFrame && iFrame && iFrameDoc && iFrameDoc.body && iFrameDoc.body.innerHTML && iFrameDoc.body.innerHTML.indexOf('##WA###BlankHTMLPageForEditorContentNew###WA##') !== -1)
    {
      reloadUserStyles();
      setStyleForEventDetailsPage();
      setTimeout(startViewingStep2, 100);

      BEditor.EditorsManager.hidePageShadingContainer();
    }
    else
    {
      if (isGecko)
      {
        if (waitIFrameCounter > 37)
        {
          BEditor.EditorsManager.showPageShadingContainer(true);
        }

        if (waitIFrameCounter > 217 * helpers.brokenIFrame.counter)
        {
          fixBrokenIFrame();
          waitIFrameCounter = 0;
        }
        else
        {
          if (objIFrame && iFrame && iFrameDoc)
          {
            waitIFrameCounter++;
          }
        }
      }

      setTimeout(startViewing, (isTB ? 17 : 100));
    }

    return true;
  }


  function startViewingStep2()
  {
    let i, k, n, elements, fragment, str, txArea;

    if (!$(iFrameId))
    {
      pThis.isObjValid = 2;
      return false;
    }

    if (isIE)
    {
      iFrame = (objIFrame ? objIFrame.contentWindow : null);

      if (iFrame)
      {
        iFrameDoc = iFrame.document;
      }
      else
      {

        setTimeout(startViewingStep2, (isTB ? 17 : 100));
      }
    }

    if (iFrameDoc && iFrameDoc.body && iFrameDoc.body.innerHTML && iFrameDoc.body.innerHTML.indexOf('##WA###BlankHTMLPageForEditorContentNew###WA##') !== -1)
    {
      iFrameHtml = iFrameDoc.querySelector('HTML');
      iFrameDoc.body.innerHTML = '';

      str = extParameters.initialContent ? extParameters.initialContent.content : (content.isInTextArea ? inContainer.value : inContainer.innerHTML);

      fragment = iFrameDoc.createElement('DIV');
      fragment.innerHTML = str;
      elements = fragment.getElementsByTagName('A');

      for (i = 0; i < elements.length; i++)
      {
        if (elements[i].href && BEditor.environment.topWindow.contentarea && BEditor.environment.topWindow.contentarea.location && elements[i].href.indexOf('#') === BEditor.environment.topWindow.contentarea.location.href.length)
        {
          elements[i].href = elements[i].href.substr(elements[i].href.indexOf('#'));
        }

        if (elements[i].href && elements[i].href.indexOf('#') !== 0 && elements[i].href.indexOf('#') !== window.location.href.length)
        {
          elements[i].setAttribute('target', '_blank', 0);
        }
      }

      str = fragment.innerHTML;
      htmlElement = iFrameDoc.querySelector('HTML');

      frameSize.init();

      if (gadgetContainer)
      {
        if (gadgetContainer.id)
        {
          htmlElement.id = gadgetContainer.id;
        }

        if (gadgetContainer.className)
        {
          htmlElement.className = gadgetContainer.className.replace(/(^|\s+)WaGadget\s*/i, '$1');
        }

        htmlElement.setAttribute('style', 'display: block; position: relative; width: auto; minWidth: 0; maxWidth: none; height: auto !important; minHeight: 0; maxHeight: none; overflow-x: auto; overflow-y: ' + (isWebKit ? 'hidden' : 'auto') + '; visibility: visible; margin: -' + frameSize.cloneEditableAreaCoords.paddingTop + 'px 0 0 -' + frameSize.cloneEditableAreaCoords.paddingLeft + 'px; padding:  ' + frameSize.cloneEditableAreaCoords.paddingTop + 'px 0 0 ' + frameSize.cloneEditableAreaCoords.paddingLeft + 'px; outline: none; border: none; background-color: transparent; background-image: none; background: none;') //TODO:Dime Added more rules
      }

      iFrameDoc.body.id = (mainIdForBodyStyles != null) ? mainIdForBodyStyles : inContainer.id;
      iFrameDoc.body.className = (mainClassForBodyStyles != null) ? mainClassForBodyStyles : inContainer.className.replace(/(^|\s+)WaGadget\s*/i, '$1');
      iFrameDoc.body.className += (currentBodyCSSClasses != null) ? ((iFrameDoc.body.className !== '') ? ' ' : '') + currentBodyCSSClasses : '';

      iFrameDoc.body.setAttribute('style', '');
      iFrameDoc.body.style.display = 'block';
      iFrameDoc.body.style.position = 'static';
      iFrameDoc.body.style.width = 'auto';
      iFrameDoc.body.style.minWidth = '0';
      iFrameDoc.body.style.maxWidth = 'none';
      iFrameDoc.body.style.cssText += 'height: auto !important';
      iFrameDoc.body.style.minHeight = '0';
      iFrameDoc.body.style.maxHeight = 'none';
      iFrameDoc.body.style.overflow = 'auto';
      iFrameDoc.body.style.overflowX = 'hidden';
      iFrameDoc.body.style.overflowY = (isIE ? 'hidden' : 'visible');
      htmlElement.style.overflowX = 'hidden';
      htmlElement.style.backgroundColor = 'transparent';
      htmlElement.style.backgroundImage = 'none';
      htmlElement.style.filter = 'none';
      iFrameDoc.body.style.filter = 'none';
      htmlElement.style[((isIE) ? 'styleFloat' : 'cssFloat')] = 'none';
      iFrameDoc.body.style[((isIE) ? 'styleFloat' : 'cssFloat')] = 'none';
      iFrameDoc.body.innerHTML = str;
      contentChecker.oldContentLength = str.length;

      if (content.isInTextArea)
      {
        iFrameDoc.body.style.background = 'none';
      }

      for (k in bodyTransferStyles)
      {
        if (bodyTransferStyles.hasOwnProperty(k))
        {
          bodyStyles[k] = {};

          for (n in bodyTransferStyles[k])
          {
            if (bodyTransferStyles[k].hasOwnProperty(n))
            {
              bodyStyles[k][n] = (isGadget && !content.isInTextArea) ? BEditor.styleHelpers.getElementStyle(inContainer, n) : bodyTransferStyles[k][n];

              if (bodyStyles[k][n] != null && ((isGecko && k === 'background') || (bodyStyles[k][n] !== '' && bodyStyles[k][n] !== BEditor.styleHelpers.getElementStyle(iFrameDoc.body, n))))
              {
                iFrameDoc.body.style[n] = bodyStyles[k][n];
              }
            }
          }
        }
      }

      if (isEditEventDetails)
      {
        setStylesForEventDetailsPage();
      }

      helpers.fixers.fixGeckoBodyBackground(true);

      style.normalizer.fixWebKitHR();

      elements = iFrameDoc.body.querySelectorAll('A');
      elements.forEach(prepareLinkInView);

      frameSize.start(true, (displayMode === 'view'));

      pThis.isObjValid = 2;

    }
    else
    {
      iFrameDoc = iFrame.document;
      setTimeout(startViewingStep2, (isTB ? 17 : 100));
    }

    function prepareLinkInView (link, ind)
    {
      let linkHref = link.getAttribute('href');

      if (linkHref && linkHref.indexOf('#') === 0)
      {
        link.setAttribute('name', 'tempName_' + ind);
        link.removeAttribute('href');
        link.style.cursor = 'pointer';

        BEditor.domHandlers.addHandler(link, 'click', function ()
        {
          let myAnchor = iFrameDoc.querySelector('A[name="' + linkHref.substring(1) + '"]'),
            myAnchorCoords,
            iFrameCoords;

          if (myAnchor)
          {
            myAnchorCoords = BEditor.dimensions.getElementXY(myAnchor, iFrame);
            iFrameCoords = BEditor.dimensions.getElementXY(objIFrame, $winContent);
            $winContent.document.querySelector('HTML').scrollTop = myAnchorCoords.top + iFrameCoords.top;
          }
        });
      }
      else if (extParameters.isLinksUnclickable)
      {
        BEditor.domHandlers.addHandler(link, 'click', BEditor.domHandlers.stopEvent);
      }
    }
  }


  this.startEditing = function()
  {
    if (!pThis.isObjValid)
    {
      return false;
    }

    try
    {
      iFrameDoc = iFrame.document;
    }
    catch (err)
    {
      if (isGecko)
      {
        setTimeout(fixBrokenIFrameForced, 3700 * helpers.brokenIFrame.counter);
        setTimeout(function() { pThis.startEditing(); }, 3700 * helpers.brokenIFrame.counter + 250 * helpers.brokenIFrame.counter);
        BEditor.EditorsManager.showPageShadingContainer(true);
        return false;
      }
    }

    if (objIFrame && iFrame && iFrameDoc && iFrameDoc.body && iFrameDoc.body.innerHTML && iFrameDoc.body.innerHTML.indexOf('##WA###BlankHTMLPageForEditorContentNew###WA##') !== -1 &&
      htmlFragmentElement && htmlFragmentWin && htmlFragmentWin.document && htmlFragmentWin.document.body && htmlFragmentWin.document.body.innerHTML && htmlFragmentWin.document.body.innerHTML.indexOf('##WA###BlankHTMLPageForEditorContentNew###WA##') !== -1)
    {
      iFrameDoc.editorId = editorId;

      if (isIE)
      {
        iFrameDoc.designMode = 'on';
      }

      htmlFragmentHtml = htmlFragmentWin.document.querySelector('HTML');
      htmlFragment = htmlFragmentWin.document.body;

      setStyleForEventDetailsPage();
      setTimeout(startEditingStep2, (isTB ? 17 : 100));

      fireEvent('starting');
      BEditor.EditorsManager.hidePageShadingContainer();
    }
    else
    {
      if (isGecko)
      {
        if (waitIFrameCounter > 37)
        {
          BEditor.EditorsManager.showPageShadingContainer(true);
        }

        if (waitIFrameCounter > 217 * helpers.brokenIFrame.counter)
        {
          fixBrokenIFrame();
          waitIFrameCounter = 0;
        }
        else
        {
          if (objIFrame && iFrame && iFrameDoc)
          {
            waitIFrameCounter++;
          }
        }
      }

      setTimeout(pThis.startEditing, (isTB ? 17 : 100));
    }

    return true;
  };


  function startEditingStep2()
  {
    let i, element, elements, fragment, text, str, txArea, cssLinks;
    let n, k;
    let templateCode,
        layoutId,
        holderName,
        cssClasses = '',
        reIsPlaceHolder = /(?:^|\s)WaPlaceHolder(?:\s|$)/i;

    if (!$(iFrameId))
    {
      pThis.isObjValid = 2;
      return false;
    }

    if (isIE)
    {
      iFrame = (objIFrame ? objIFrame.contentWindow : null);
      htmlFragmentWin = (htmlFragmentElement ? htmlFragmentElement.contentWindow : null);

      if (iFrame && (!isGadget || htmlFragmentWin))
      {
        iFrameDoc = iFrame.document;
      }
      else
      {
        setTimeout(startEditingStep2, (isTB ? 17 : 100));
      }
    }

    if (iFrameDoc && iFrameDoc.body && iFrameDoc.body.innerHTML && iFrameDoc.body.innerHTML.indexOf('##WA###BlankHTMLPageForEditorContentNew###WA##') !== -1 &&
      htmlFragmentWin.document && htmlFragmentWin.document.body && htmlFragmentWin.document.body.innerHTML && htmlFragmentWin.document.body.innerHTML.indexOf('##WA###BlankHTMLPageForEditorContentNew###WA##') !== -1)
    {
      iFrameHtml = iFrameDoc.querySelector('HTML');
      iFrameDoc.body.innerHTML = '';

      iFrameHtml.setAttribute('data-wacontent', '0');
      iFrameHtml.setAttribute('data-wacontenttag', 'HTML');
      iFrameDoc.body.setAttribute('data-wacontent', '1');
      iFrameDoc.body.setAttribute('data-wacontenttag', 'BODY');

      htmlFragmentHtml = htmlFragmentWin.document.querySelector('HTML');
      htmlFragment = htmlFragmentWin.document.body;
      htmlFragment.innerHTML = '';

      reloadUserStyles(true);

      if (isIE > 8) //TODO Dime ???!!! Fix for IE8
      {
        element = document.createElement('STYLE');
        element.setAttribute('type', 'text/css');
        element.innerHTML = 'DIV[style*="absolute"] { outline: 1px dashed #999999; }';
        iFrameDoc.head.appendChild(element);
      }

      str = extParameters.initialContent ? extParameters.initialContent.content : (content.isInTextArea ? inContainer.value : inContainer.innerHTML);
      str = removeBrokenPasteContainer(str);
      str = style.normalizer.removeWaWebKitSavedSpanIndex(str);
      content.saveRawContent = str;

      frameSize.init();

      content.isRawContentEmpty = (str.replace(/\s*(?:&nbsp;|<br>)?\s*/i, '') === '');

      if (isIE)
      {
        if (content.isRawContentEmpty)
        {
          str = '<p>&nbsp;</p>';
        }
        else
        {
          str = str.replace(/(<p(?:\s[^>]*>|>))((?:<(?:b|i|stong|em|u|sup|sub|strike|font|span)(?:\s[^>]*>|>))*)<br>((?:<\/(?:b|i|stong|em|u|sup|sub|strike|font|span)>)*)(<\/p>)/i, '$1$2&nbsp;$3$4');
        }
      }
      else
      {
        if (content.isRawContentEmpty)
        {
          str = '<p><br></p>';
        }
        else
        {
          str = str.replace(/(<p(?:\s[^>]*>|>))((?:<(?:b|i|stong|em|u|sup|sub|strike|font|span)(?:\s[^>]*>|>))*)<br>((?:<\/(?:b|i|stong|em|u|sup|sub|strike|font|span)>)*)(<\/p>)/i, '$1$2<br>$3$4');
        }
      }

      if (isWebKit)
      {
        str = style.normalizer.restoreWebKitHTMLInline(str);
      }

      str = str.replace(/(<a\s[^>]*)\s?target=['"]?_blank['"]?\s?([^>]*)\sadmtarget=['"]?true['"]?\s?([^>]*>)/ig, "$1$2$3");
      str = str.replace(/(<a\s[^>]*)\s?admtarget=['"]?true['"]?\s?([^>]*)\starget=['"]?_blank['"]?\s?([^>]*>)/ig, "$1$2$3");
      str = str.replace(/(<a\s[^>]*)\s>/ig, "$1>");

      str = helpers.userJavascript.collect(str, true, false);
      str = anchor.collectAnchors(str);
      str = anchor.fixLinksToAnchors(str);

      if (isEditorInHeader)
      {
        str = str.replace(/<span(\s[^<>]*idHeaderAssociationName[^<>]*>[^<>]*)<\/span>/ig, '<div$1</div>');
        fragment = iFrameDoc.createElement('DIV');
        fragment.innerHTML = str;
        elements = fragment.getElementsByTagName('*');
        i = 0;

        while (i < elements.length)
        {
          if (!elements[i].id && elements[i].getAttribute('data-autoabs') && $Style(elements[i], 'position') === 'absolute' ||
             (elements[i].innerHTML.replace(/[\s\r\n]+/i, '') === '' && (elements[i].nodeName !== 'BR' || elements[i].nodeName !== 'IMG')))
          {
            elements[i].parentNode.removeChild(elements[i]);
          }
          else
          {
            i++;
          }
        }

        text = fragment.innerHTML;

        if (text.replace(/[\s\r\n]+/i, '') === '')
        {
          str += '&nbsp;';
        }
      }

      htmlElement = iFrameDoc.querySelector('HTML');

      if (isGadget)
      {
        element = placeHolder ? placeHolder.parentNode : (gadgetContainer ? gadgetContainer.parentNode : null);

        while (element && element.nodeName !== 'BODY')
        {
          if (reIsPlaceHolder.test(element.className))
          {
            cssClasses = cssClasses ? element.className : element.className + ' ' + cssClasses;
          }

          element = element.parentNode;
        }

        if (placeHolder)
        {
          if (placeHolder.id)
          {
            htmlElement.id = placeHolder.id;
          }

          if (placeHolder.className)
          {
            cssClasses += (cssClasses ? ' ' : '') + placeHolder.className;
          }
        }

        htmlElement.className = cssClasses.replace(/(^|\s)WaPlaceHolder(?:\s|$)/ig, '$1');
        htmlElement.setAttribute('style', 'display: block; position: relative; width: auto; min-width: 0; max-width: none; height: auto !important; min-height: 0; max-height: none; overflow-x: auto; overflow-y: ' + (isWebKit ? 'hidden' : 'auto') + '; visibility: visible; margin: -' + frameSize.cloneEditableAreaCoords.paddingTop + 'px 0 0 -' + frameSize.cloneEditableAreaCoords.paddingLeft + 'px; padding: ' + frameSize.cloneEditableAreaCoords.paddingTop + 'px 0 0 ' + frameSize.cloneEditableAreaCoords.paddingLeft + 'px; outline: none; border: none; background-color: transparent; background-image: none; background: none;'); //TODO:Dime Added more rules
      }

      iFrameDoc.querySelector('HTML').style.overflowY = (isWebKit ? 'hidden' : 'auto');

      iFrameDoc.body.id = (mainIdForBodyStyles != null) ? mainIdForBodyStyles : inContainer.id;
      iFrameDoc.body.setAttribute('data-waeditorid', editorId);
      matchShowTablesBordersCookie = document.cookie.match(/ShowTablesBordersInEditor=(\d)/);
      isShowTablesBordersCookie = (matchShowTablesBordersCookie != null && matchShowTablesBordersCookie.length > 1) ? matchShowTablesBordersCookie[1] : 1;

      additionalBodyClassName = '';

      if (isShowTablesBordersCookie === 1)
      {
        additionalBodyClassName = 'controlTablesBordersInEditor';
      }

      matchShowAnchorCookie = document.cookie.match(/ShowAnchorsInEditor=(\d)/);
      isShowAnchorCookie = (matchShowAnchorCookie != null && matchShowAnchorCookie.length > 1) ? matchShowAnchorCookie[1] : 1;

      if (isShowAnchorCookie !== 1) //TODO:Dime ???
      {
        additionalBodyClassName += (additionalBodyClassName ? ' ' : '') + ' controlVisibilityAnchorInEditor';
      }

      if (isGadget && gadgetContainer)
      {
        if (gadgetContainer.id)
        {
          iFrameDoc.body.id = gadgetContainer.id;
        }

        if (gadgetContainer.className)
        {
          iFrameDoc.body.className = gadgetContainer.getAttribute('class') ? gadgetContainer.getAttribute('class').replace(/(^|\s+)WaGadget\s*/i, '$1') : '';
          iFrameDoc.body.className += ((iFrameDoc.body.getAttribute('class') && cloneEditableArea.getAttribute('class')) ? ' ' : '') + cloneEditableArea.getAttribute('class');
        }

        gadgetContainer.style.overflow = 'inherit'; //TODO Dime ???!!! Fix for IE8
        setTimeout(function () { gadgetContainer.style.overflow = 'hidden'; }, 100);

        iFrameDoc.body.setAttribute('style', (gadgetContainer.getAttribute('style') || '')); //TODO:Dime Added more rules
        iFrameDoc.body.style.display = 'block';
        iFrameDoc.body.style.position = 'static';
        iFrameDoc.body.style[((isIE) ? 'styleFloat' : 'cssFloat')] = 'none';
        iFrameDoc.body.style.visibility = 'visible';
        iFrameDoc.body.style.width = 'auto';
        iFrameDoc.body.style.minWidth = '0';
        iFrameDoc.body.style.maxWidth = 'none';
        iFrameDoc.body.style.height = '';
        iFrameDoc.body.style.cssText += 'height: auto !important';
        iFrameDoc.body.style.minHeight = '0';
        iFrameDoc.body.style.maxHeight = 'none';
        iFrameDoc.body.style.overflow = 'auto';
        iFrameDoc.body.style.overflowX = !frameSize.isEditorNoScroll ? (!isWebKit ? 'auto' : 'visible') : 'hidden';
        iFrameDoc.body.style.overflowY = (isIE ? 'hidden' : 'visible');
        iFrameDoc.body.style.margin = 0;
        iFrameDoc.body.style.padding = 0;
        iFrameDoc.body.style.outline = 'none';
        iFrameDoc.body.style.border = 'none';
      }
      else
      {
        iFrameDoc.body.className = ((mainClassForBodyStyles != null) ? mainClassForBodyStyles + ' ' : (inContainer.className ? inContainer.className.replace(/(^|\s+)WaGadget\s*/i, '$1') + ' ' : ''));

        iFrameDoc.body.setAttribute('style', '');
        iFrameDoc.body.style.display = 'block';
        iFrameDoc.body.style.position = 'static';

        iFrameDoc.body.style.width = 'auto';
        iFrameDoc.body.style.minWidth = '0';
        iFrameDoc.body.style.maxWidth = 'none';
        iFrameDoc.body.style.cssText += 'height: auto !important';
        iFrameDoc.body.style.minHeight = '0';
        iFrameDoc.body.style.maxHeight = 'none';

        iFrameDoc.body.style.overflow = 'auto';
        iFrameDoc.body.style.overflowX = !frameSize.isEditorNoScroll ? 'auto' : 'hidden';
        iFrameDoc.body.style.overflowY = (isIE ? 'hidden' : 'visible');
        iFrameDoc.body.style[((isIE) ? 'styleFloat' : 'cssFloat')] = 'none';
      }

      iFrameDoc.body.className += (iFrameDoc.body.getAttribute('class') ? ' ' : '') + additionalBodyClassName;
      iFrameDoc.body.className += (currentBodyCSSClasses != null) ? ((iFrameDoc.body.className !== '') ? ' ' : '') + currentBodyCSSClasses : '';

      htmlElement.style.overflowX = 'auto';
      htmlElement.style[((isIE) ? 'styleFloat' : 'cssFloat')] = 'none';

      iFrameDoc.body.style.filter = 'none';

      if (content.isInTextArea)
      {
        iFrameDoc.body.style.background = 'none';
      }

      for (k in bodyTransferStyles)
      {
        if (bodyTransferStyles.hasOwnProperty(k))
        {
          bodyStyles[k] = {};

          for (n in bodyTransferStyles[k])
          {
            if (bodyTransferStyles[k].hasOwnProperty(n))
            {
              bodyStyles[k][n] = (isGadget && !content.isInTextArea) ? BEditor.styleHelpers.getElementStyle(inContainer, n) : bodyTransferStyles[k][n];

              if (bodyStyles[k][n] != null && ((isGecko && k === 'background') || (bodyStyles[k][n] !== '' && bodyStyles[k][n] !== BEditor.styleHelpers.getElementStyle(iFrameDoc.body, n))))
              {
                iFrameDoc.body.style[n] = bodyStyles[k][n];
              }
            }
          }
        }
      }

      if (isEditEventDetails)
      {
        setStylesForEventDetailsPage();
      }

      if (pParent.getTemplateCode)
      {
        templateCode = pParent.getTemplateCode();

        if (templateCode)
        {
          htmlElement.setAttribute('templatecode', templateCode);
        }
      }

      if (pParent.getLayoutId)
      {
        layoutId = pParent.getLayoutId();

        if (layoutId)
        {
          iFrameDoc.body.setAttribute('data-layoutid', layoutId);
        }
      }

      if (pParent.getHolderName)
      {
        holderName = pParent.getHolderName(editorId);

        if (holderName)
        {
          iFrameDoc.body.setAttribute('data-cellinner', holderName);
        }
      }

      str = setWaContentMarker(str);
      iFrameDoc.body.innerHTML = str;
      contentChecker.oldContentLength = str.length;

      helpers.fixers.removeBrokenInputMarkers();
      image.objectFactory.checkAllImages();
      helpers.fixers.fixGeckoBodyBackground(true);
      helpers.fixers.setFixGeckoTables({isFix: true});
      helpers.fixers.fixGeckoAlign();
      style.normalizer.fixWebKitHR();
      contentCleaner.removeEmptyP();
      layer.objectFactory.fixAllAbsElements();

      if (isIE)
      {
        iFrameDoc.execCommand('2D-Position', false, true);

        if (!iFrame.isEditorMainHandlersAdded)
        {
          iFrameDoc.attachEvent('onmousedown', mouse.mouseDown);
          iFrameDoc.attachEvent('onmouseup', mouse.mouseUp);
          iFrame.attachEvent('onblur', function() { pauseEditing(); });
          iFrame.attachEvent('onfocus', function() { continueEditing(); });
          iFrameDoc.attachEvent('onmouseup', function() { pToolbar.refreshState(); });
          iFrameDoc.attachEvent('onclick', mouse.mouseClick);
          iFrameDoc.attachEvent('onkeydown', function() { key.keyDown(iFrame.event); });
          iFrameDoc.attachEvent('onkeyup', function() { key.keyUp(iFrame.event); });
          iFrameDoc.body.attachEvent('oncut', function() { setTimeout(contentChecker.onCutHandler, 100); });
          iFrameDoc.body.attachEvent('ondrop', function() { setTimeout(contentChecker.onPasteHandler, 100); });
          iFrame.attachEvent('onunload', unload);
          iFrameDoc.body.attachEvent('oncopy', copyPaste.generic.onCopy);
          iFrameDoc.body.attachEvent('onpaste', copyPaste.generic.onPaste);
          iFrame.isEditorMainHandlersAdded = true;
        }

        BEditor.domHandlers.addHandler(BEditor.environment.topWindow.document, 'click', prePauseEditing);

        if (BEditor.environment.topWindow.contentarea)
        {
          BEditor.domHandlers.addHandler(BEditor.environment.topWindow.contentarea.document, 'click', prePauseEditing);
        }
      }
      else
      {
        setTimeout(function()
        {
          if (startEditingStep3 && typeof (startEditingStep3) === 'function')
          {
            startEditingStep3();
          }
        }, 1000);

        iFrameDoc.designMode = 'on';

        if (!iFrame.isEditorMainHandlersAdded)
        {
          if (isWebKit)
          {
            if (!(BEditor.device.isIOS && BEditor.device.iOSVersion[0] > 7 && BEditor.browser.isWebKitSafari))
            {
              iFrame.addEventListener('blur', function() { pauseEditing(); }, false);
            }
            else
            {
              iFrame.addEventListener('blur', function() { pauseEditingWait(); }, false);
            }

            iFrame.addEventListener('focus', function() { continueEditing(); }, false);
            iFrame.addEventListener('focus', function()
            {
              if (typeof ($winContent.DataChangeWatcher) !== 'undefined')
              {
                $winContent.setTimeout($winContent.DataChangeWatcher.resumeWatching, 100, 'BEditor.EditorsManager');
              }
            }, false);

              if (BEditor.browser.isWebKitSafari)
              {
                iFrameDoc.addEventListener('copy', copyPaste.generic.onCopy, false);
                iFrameDoc.addEventListener('paste', copyPaste.generic.onPaste, false);
              }
              else
              {
                iFrameDoc.addEventListener('copy', copyPaste.webkit.webkitCopy, false);
                iFrameDoc.addEventListener('paste', copyPaste.webkit.webkitPaste, false);
              }
          }
          else
          {
            iFrameDoc.addEventListener('blur', function() { pauseEditing(); }, false);
            iFrameDoc.addEventListener('focus', function() { continueEditing(); }, false);
            iFrameDoc.addEventListener('focus', function()
            {
              if (typeof ($winContent.DataChangeWatcher) !== 'undefined')
              {
                $winContent.setTimeout($winContent.DataChangeWatcher.resumeWatching, 100, 'BEditor.EditorsManager');
              }
            }, false);

            //if (!BEditor.Browser.isEdge)
            //{
              iFrameDoc.addEventListener('copy', copyPaste.generic.onCopy, false);
              iFrameDoc.addEventListener('paste', copyPaste.generic.onPaste, false);
            //}
          }

          objIFrame.contentDocument.defaultView.addEventListener('focus', function() { continueEditing(); }, false);

          iFrameDoc.addEventListener('mousedown', mouse.mouseDown, false);
          iFrameDoc.addEventListener('mouseup', mouse.mouseUp, false);
          iFrameDoc.addEventListener('mouseup', function() { pToolbar.refreshState(); }, false);
          iFrameDoc.addEventListener('click', mouse.mouseClick, false);

          if (BEditor.device.isIOS)
          {
            element = cutBox;

            while (element.parentNode && element.parentNode.nodeName !== 'BODY')
            {
              element = element.parentNode;
            }

            element.addEventListener('click', function () {}, false);
          }

          iFrameDoc.addEventListener('keydown', key.keyDown, false);
          iFrameDoc.addEventListener('keyup', key.keyUp, false);
          iFrameDoc.addEventListener('keypress', key.keyPress, false);
          iFrameDoc.addEventListener('unload', unload, false);
          iFrameDoc.addEventListener('dragdrop', mouse.mouseDragDrop, false);
          iFrameDoc.addEventListener('drop', mouse.mouseDragDrop, false);
          iFrameDoc.body.addEventListener('cut', function() { setTimeout(contentChecker.onCutHandler, 100); }, false);

          iFrame.isEditorMainHandlersAdded = true;
        }

        setTimeout(function()
        {
          try
          {
            iFrameDoc.execCommand('styleWithCSS', false, false);

            if (BEditor.browser.isGecko)
            {
              iFrameDoc.execCommand('enableObjectResizing', false, true);
              iFrameDoc.execCommand('enableInlineTableEditing', false, true);
            }
          }
          catch (err) {}
        }, 500);
      }

      setTimeout(layer.helpers.setAbsElementZIndexCounter, 137);

      setTimeout(function() { if (!checkContentTM) { contentChecker.currentEditorContent = iFrameDoc.body.innerHTML; undoRedo.init(); setTimeout(contentChecker.checkContentChanged, 500); } }, 500);

      frameSize.start(true, (displayMode === 'view'));
      range.startCheckSelection();

      if (autoFocus)
      {
        setAutoFocus();
      }

      htmlFragmentHtml.id = htmlElement.id;
      htmlFragmentHtml.setAttribute('class', (htmlElement.getAttribute('class') || ''));
      htmlFragmentHtml.setAttribute('style', (htmlElement.getAttribute('style') || ''));

      htmlFragment.id = iFrameDoc.body.id;
      htmlFragment.setAttribute('class', (iFrameDoc.body.getAttribute('class') || ''));
      htmlFragment.setAttribute('style', (iFrameDoc.body.getAttribute('style') || ''));

      setObjValidTM = setTimeout(setObjValid, 17);
    }
    else
    {
      iFrameDoc = iFrame.document;

      setTimeout(startEditingStep2, (isTB ? 17 : 100));
    }
  }


  function startEditingStep3()
  {
    if (iFrameDoc && iFrameDoc.body && iFrameDoc.body.innerHTML && iFrameDoc.body.innerHTML.toLowerCase().match(/^\s*blank\s*$/) && iFrameDoc.designMode)
    {
      iFrameDoc.designMode = 'on';
    }
  }


  function reloadUserStyles (isReloadHtmlFragmentStyles)
  {
    let i,
        rnd,
        elements;

    if (window.EditorToolbarComposeEmailConfig && pToolbarConfig.name === EditorToolbarComposeEmailConfig.name)
    {
      return;
    }

    rnd = (new Date()).getTime();
    elements = iFrameDoc.querySelectorAll('LINK[href*="/resources/theme/customStyles.css"], LINK[href*="/resources/theme/user.css"]');

    for (i = 0; i < elements.length; i++)
    {
      elements[i].setAttribute('href', elements[i].getAttribute('href') + '_' + rnd)
    }

    if (isReloadHtmlFragmentStyles && htmlFragmentWin.document)
    {
      elements = htmlFragmentWin.document.querySelectorAll('LINK[href*="/resources/theme/customStyles.css"], LINK[href*="/resources/theme/user.css"]');

      for (i = 0; i < elements.length; i++)
      {
        elements[i].setAttribute('href', elements[i].getAttribute('href') + '_' + rnd)
      }
    }
  }


  function changeBodyCSSClasses ()
  {
    let i, k,
        contentStyles,
        className,
        currentClasses,
        currentBodyClasses,
        bodyClasses;

    if (pParent.getContentStyles)
    {
      contentStyles = pParent.getContentStyles();
    }

    className = (contentStyles && contentStyles.cssClasses != null) ? contentStyles.cssClasses : '';

    if (pThis.isObjValid !== 2)
    {
      currentBodyCSSClasses = className;
      return;
    }

    currentClasses = (currentBodyCSSClasses != null) ? currentBodyCSSClasses.split(/\s+/) : [];
    currentBodyClasses = iFrame.document.body.className.split(/\s+/);

    for (i = 0; i < currentClasses.length; i++)
    {
      while ((k = currentBodyClasses.indexOf(currentClasses[i])) !== -1)
      {
        currentBodyClasses.splice(k, 1);
      }
    }

    bodyClasses = currentBodyClasses.join(' ');
    iFrame.document.body.className = bodyClasses;
    currentBodyCSSClasses = className;

    if (currentBodyCSSClasses)
    {
      iFrame.document.body.className += ((bodyClasses !== '') ? ' ' : '') + currentBodyCSSClasses;
    }
  }


  function iosCheckRefreshState ()
  {
    iosCurrentImage = image.getCurrentImage();
    iosCurrentRange = range.getCurrent();

    if (iosCurrentRange)
    {
      if (iosOldRange.currentImage !== iosCurrentImage || iosCurrentRange.startContainer !== iosOldRange.startContainer || iosCurrentRange.startOffset !== iosOldRange.startOffset || iosCurrentRange.endContainer !== iosOldRange.endContainer || iosCurrentRange.endOffset !== iosOldRange.endOffset)
      {
        iosOldRange.currentImage = iosCurrentImage;
        iosOldRange.startContainer = iosCurrentRange.startContainer;
        iosOldRange.startOffset = iosCurrentRange.startOffset;
        iosOldRange.endContainer = iosCurrentRange.endContainer;
        iosOldRange.endOffset = iosCurrentRange.endOffset;

        range.save();

        if (!style.formatPainter.isIOSStopPasteStyle && style.formatPainter.copyTextStyle.currentStyle != null)
        {
          style.formatPainter.pasteStyle();
        }

        pToolbar.refreshState();
      }
    }
  }


  function setObjValid ()
  {
    waitScore = 0;
    pThis.isObjValid = 2;

    pToolbar.editorStarting(pThis);

    fireEvent('started');
    pThis.EditorStarted.fireHandlers({ 'editingElement': (isGadget ? cloneEditableArea : cutBox) });
  }


  function stopEdit()
  {
    let htmlContent = content.getData().htmlContent;

    unload();

    if (isGadget)
    {
      cloneEditableArea.style.display = 'none';
    }

    cutBox.style.display = 'none';

    inContainer.style.display = 'block';

    if (inContainer.id && inContainer.id.indexOf('_editableArea_waEditorTmpId') !== -1)
    {
      inContainer.removeAttribute('id');
    }

    pauseEditing(true);
    pToolbar.editorStopped();
    BEditor.stopDragDropUploaderWatch();
    pThis.EditorStopped.fireHandlers({ 'editorContent': htmlContent });
  }





  function removeBrokenPasteContainer (content)
  {
    let i,
        fragment,
        elements;

    if (!(/data-wawebkitpastecontainer/i).test(content))
    {
      return content;
    }

    fragment = iFrameDoc.createElement('DIV');
    fragment.innerHTML = content;
    elements = fragment.querySelectorAll('DIV[data-wawebkitpastecontainer]');

    for (i = elements.length; i--;)
    {
      elements[i].outerHTML = (elements[i].style.opacity == null || elements[i].style.opacity === '' || parseFloat(elements[i].style.opacity) !== 0) ? elements[i].innerHTML : '';
    }

    return fragment.innerHTML;
  }


  let setStyleForEventDetailsPageCounter = null,
      backgroundStyleForEventDetailsPage = null,
      bodyStylesForEventDetailsPage = null;


  function setStyleForEventDetailsPage ()
  {
    isEditEventDetails = !!($('idPrimaryContentBlock1ContentHolderEventAdmin'));

    bodyStylesForEventDetailsPage = null;

    if (isEditEventDetails)
    {
      if (setStyleForEventDetailsPageCounter === null)
      {
        setStyleForEventDetailsPageCounter = 0;
      }

      BEditor.getRealBackgroundForEventDetails(setStyleForEventDetailsPageStep1, bodyTransferStyles['text']);
    }
  }


  function setStyleForEventDetailsPageStep1 (backgroundStyle, bodyStyles)
  {
    if (backgroundStyle === '##WA_NEWEVENTWITHEMPTYURL##')
    {
      bodyStylesForEventDetailsPage = {};
      return;
    }

    if (!backgroundStyle || !bodyStyles)
    {
      if (setStyleForEventDetailsPageCounter < 7)
      {
        setStyleForEventDetailsPageCounter++;
        setTimeout(setStyleForEventDetailsPage, 100 * setStyleForEventDetailsPageCounter);
      }
      else
      {
        setStyleForEventDetailsPageCounter = null;
        backgroundStyleForEventDetailsPage = {};
        bodyStylesForEventDetailsPage = {};
      }

      return;
    }

    backgroundStyleForEventDetailsPage = backgroundStyle || {};
    bodyStylesForEventDetailsPage = bodyStyles || {};
  }


  function setStylesForEventDetailsPage ()
  {
    let k,
        targetElement;

    if (!backgroundStyleForEventDetailsPage || !bodyStylesForEventDetailsPage)
    {
      setTimeout(setStylesForEventDetailsPage, 50);
      return;
    }

    targetElement = $('idPrimaryContentBlock1ContentHolderEventAdmin');

    if (targetElement)
    {
      for (k in backgroundStyleForEventDetailsPage)
      {
        if (backgroundStyleForEventDetailsPage.hasOwnProperty(k))
        {
          targetElement.style[k] = backgroundStyleForEventDetailsPage[k];
        }
      }
    }

    for (k in bodyStylesForEventDetailsPage)
    {
      if (bodyStylesForEventDetailsPage.hasOwnProperty(k))
      {
        if (bodyStylesForEventDetailsPage[k] != null && (bodyStylesForEventDetailsPage[k] !== '' && bodyStylesForEventDetailsPage[k] !== BEditor.styleHelpers.getElementStyle(iFrameDoc.body, k)))
        {
          iFrameDoc.body.style[k] = bodyStylesForEventDetailsPage[k];
        }
      }
    }
  }


  function setAutoFocus(isAutoFocusEnd)
  {
    if (isTB)
    {
      setTimeout(function() { if (window && window.focus) window.focus(); }, 17);
      setTimeout(function() { if (iFrame && iFrame.focus) iFrame.focus(); }, 77);
      setTimeout(function() { if (iFrame && iFrame.focus) iFrame.focus(); }, waitScore + 173);
    }
    else
    {
      setTimeout(function() { if (window && window.focus) window.focus(); }, waitScore + 250);
      setTimeout(function() { if (iFrame && iFrame.focus) iFrame.focus(); }, waitScore + 300);
    }

    if (isAutoFocusEnd || autoFocus.toLowerCase() === 'end')
    {
      setTimeout(setAutoFocusEnd, (isTB ? 173 : 350));
    }
  }


  function setAutoFocusEnd()
  {
    let selection, objRange;

    if (isIE)
    {
      objRange = iFrameDoc.body.createTextRange();
      objRange.moveToElementText(iFrameDoc.body);
      objRange.collapse(false);
      objRange.select();
    }
    else
    {
      selection = iFrame.getSelection();
      selection.removeAllRanges();
      objRange = iFrameDoc.createRange();
      objRange.selectNodeContents(iFrameDoc.body);
      objRange.collapse(false);
      selection.addRange(objRange);
    }
  }


  this.focus = function(isImmediate, isSetCursorNew)
  {
    isSetCursor = isSetCursorNew;

    if (iFrame && iFrame.focus)
    {
      if (!isImmediate && waitScore)
      {
        if (isTB)
        {
          setTimeout(function() { if (iFrame && iFrame.focus) iFrame.focus(); }, 17);
        }

        setTimeout(function() { if (iFrame && iFrame.focus) iFrame.focus(); }, waitScore);
      }
      else
      {
        iFrame.focus();
        continueEditing();
        setTimeout(function() { if (iFrame && iFrame.focus) iFrame.focus(); }, 17);
      }
    }
  };


  this.blur = function()
  {
    if (iFrame && iFrame.blur)
    {
      iFrame.blur();
    }
  };


  this.getEditorId = function()
  {
    return editorId;
  };


  this.getEditorToolbarConfig = function()
  {
    if (!pToolbarConfig)
    {
      pToolbarConfig = BEditor.environment.topWindow.EditorToolbarDefaultConfig; //TODO:Dime ???
    }

    return pToolbarConfig;
  };


  function prePauseEditing ()
  {
    pauseEditing();
    isDropdownOpen = false;
  }


  let pauseEditingTM = null;

  function pauseEditingWait ()
  {
    if (!isDropdownOpen)
    {
      if (setIOSCheckRefreshStateTM) clearInterval(setIOSCheckRefreshStateTM);
      if (pauseEditingTM) clearTimeout(pauseEditingTM);
      pauseEditingTM = setTimeout(pauseEditing, 500);
    }
  }


  let prevImmediateUnreg;

  function pauseEditing (immediateUnreg)
  {
    if (pauseEditingTM) clearTimeout(pauseEditingTM);
    BEditor.appOutHandlerOnBlur(null, iFrame);

    if (!pThis.isObjValid || pThis.isObjValid < 2)
    {
      return false;
    }

    if (isIE && isDropdownOpen && !immediateUnreg)
    {
      return;
    }

    if (BEditor.device.isIOS && BEditor.device.iOSVersion[0] > 7 && BEditor.browser.isWebKitSafari && pToolbar.isClick && !immediateUnreg)
    {
      return;
    }

    if (isPersistEditing)
    {
      if (isIE && isExternalPersistEditing)
      {
        isExternalPersistEditing = false;
        return false;
      }

      isPersistEditing = false;
      return false;
    }

    frameSize.stop();
    range.stopCheckSelection();

    if (checkInsertedContainerTM) clearInterval(checkInsertedContainerTM);
    if (setIsDropdownOpenTM) clearTimeout(setIsDropdownOpenTM);
    if (setIOSCheckRefreshStateTM) clearTimeout(setIOSCheckRefreshStateTM);
    BEditor.globalHandlers.removeGlobalHandler('EditorGlobalMouseMoveInAllWindow');

    if (checkContentTM) clearInterval(checkContentTM);
    checkContentTM = null;

    prevImmediateUnreg = !!immediateUnreg;

    if (pToolbar)
    {
      if (immediateUnreg)
      {
        isDropdownOpen = false;
        pToolbar.unregEditorInstanceDo(pThis);
      }
      else
      {
        pToolbar.unregEditorInstance(pThis);
      }
    }

    isFocused = false;

    if (pToolbar)
    {
      pToolbar.refreshState();
    }

    fireEvent('blur');
    pThis.EditorBlur.fireHandlers();
  }


  function editorUnreged ()
  {
    isDropdownOpen = false;
    fireEvent('unreged');

    if (!stopBluredEvent)
    {
      pThis.EditorBlured.fireHandlers();
    }
  }


  function continueEditing ()
  {
    if (isFocused)
    {
      return true;
    }

    BEditor.appOutHandlerOnFocus(null, iFrame);

    if (!pThis.isObjValid || pThis.isObjValid < 2)
    {
      return false;
    }

    if (BEditor.device.isIOS && BEditor.device.iOSVersion[0] > 7 && BEditor.browser.isWebKitSafari && pToolbar.isClick)
    {
      range.restore();
      if (setIOSCheckRefreshStateTM) clearInterval(setIOSCheckRefreshStateTM);
      iosOldRange = {};
      setIOSCheckRefreshStateTM = setInterval(iosCheckRefreshState, 50);
      return;
    }

    isPersistEditing = false;

    if (checkInsertedContainerTM) clearInterval(checkInsertedContainerTM);
    if (setIOSCheckRefreshStateTM) clearInterval(setIOSCheckRefreshStateTM);

    frameSize.start(false);
    range.startCheckSelection();

    checkInsertedContainerTM = setInterval(contentCleaner.checkInsertedContainer, 111);

    if (BEditor.device.isIOS && BEditor.device.iOSVersion[0] > 7 && BEditor.browser.isWebKitSafari)
    {
      iosOldRange = {};
      setIOSCheckRefreshStateTM = setInterval(iosCheckRefreshState, 50);
    }

    BEditor.globalHandlers.addGlobalHandler(((BEditor.browser.isIE && BEditor.browser.isIE < 9) ? 'document' : 'window'), 'mousemove', mouse.globalMouseMove, 'EditorGlobalMouseMoveInAllWindow', {});

    if (!undoRedo.init())
    {
      undoRedo.saveUndo(true);
    }

    contentChecker.currentEditorContent = iFrameDoc.body.innerHTML;
    if (checkContentTM) clearInterval(checkContentTM);
    checkContentTM = setInterval(contentChecker.checkContentChanged, 333);

    if (isSetCursor)
    {
      range.setCursorPoint(iFrameDoc.body, (isSetCursor === 1));
      isSetCursor = 0;
    }

    range.normalizeCursor();

    pToolbar.regEditorInstance(pThis);
    isFocused = true;

    pToolbar.refreshState();
    BEditor.startDragDropUploaderWatch(pThis);

    fireEvent('focus');
    pThis.EditorFocus.fireHandlers();
  }


  function persistEditing ()
  {
    isPersistEditing = true;
    pThis.focus();
    setAutoFocus(true);
  }


  function resetPersistEditing ()
  {
    isPersistEditing = false;
  }


  function extRefreshState ()
  {
    let i;

    for (i = extRefreshStateButtons.length; i--;)
    {
      pToolbar.refreshStateButton(extRefreshStateButtons[i]);
    }
  }


  function setWaContentMarker (content)
  {
    let i,
      elements;

    if (content != null)
    {
      content = content.replace(/<([A-Z][^>]+(?:alt|title)\s*=\s*(?:"[^"]*(?:[<>][^"]?)+[^"]*"|'[^']*(?:[<>][^']?)+[^']*')(?:[^>]+(?:alt|title)\s*=\s*(?:"[^"]*(?:[<>][^"]?)+[^"]*"|'[^']*(?:[<>][^']?)+[^']*'))?[^>]*)>/ig, function (str, innerStr)
      {
        return '<' + innerStr.replace(/</g, '#WA#LT#WA#').replace(/>/g, '#WA#GT#WA#') + '>';
      });

      content = content.replace(/<([A-Za-z0-9]+)(?=\s|>)/g, function (str, tag)
      {
        return str + ' data-wacontent="' + (waContentMarkerCounter++) + '" data-wacontenttag="' + tag.toUpperCase() + '"';
      });

      return content.replace(/#WA#LT#WA#/ig, '<').replace(/#WA#GT#WA#/ig, '>');
    }
    else
    {
      elements = iFrameDoc.body.querySelectorAll(':not([data-wacontent])');

      for (i = elements.length; i--;)
      {
        elements[i].setAttribute('data-wacontent', waContentMarkerCounter++);
        elements[i].setAttribute('data-wacontenttag', elements[i].tagName);
      }
    }
  }


  function removeWaContentMarker (content)
  {
    let i,
        elements;

    if (content != null)
    {
      return content.replace(/\s+data-wacontent(?:tag)?\s*=\s*['"]?[A-Z0-9]+['"]?/ig, '');
    }
    else
    {
      elements = iFrameDoc.body.querySelectorAll('[data-wacontent],[data-wacontenttag]');

      for (i = elements.length; i--;)
      {
        elements[i].removeAttribute('data-wacontent');
        elements[i].removeAttribute('data-wacontenttag');
      }
    }
  }


  this.stopEditing = function()
  {
    if (!pThis.isObjValid || pThis.isObjValid < 2)
    {
      return false;
    }

    frameSize.stop();
    range.stopCheckSelection();

    if (checkInsertedContainerTM) clearInterval(checkInsertedContainerTM);
    if (setIOSCheckRefreshStateTM) clearInterval(setIOSCheckRefreshStateTM);

    if (checkContentTM)
    {
      clearInterval(checkContentTM);
      checkContentTM = null;
    }

    return true;
  };


  this.execCommand = function(pButton, command, params)
  {
    if (!pThis.isObjValid || pThis.isObjValid < 2)
    {
      return false;
    }

    let i, parameters;

    image.execCurrentImage = image.getCurrentImage();

    this.focus();

    if (hCommand[command])
    {
      if (hCommand[command].handler)
      {
        parameters = (params) ? params : hCommand[command].params;
        hCommand[command].handler(pButton, parameters, hCommand[command].args);
      }
      else if (hCommand[command].length)
      {
        for (i = 0; i < hCommand[command].length; i++)
        {
          parameters = (params) ? params : hCommand[command][i].params;
          hCommand[command][i].handler(pButton, parameters, hCommand[command][i].args);
        }
      }

      contentChecker.setExcludeWordCheck();

      if (command !== 'cleanupHTML')
      {
        undoRedo.saveUndo(true);
      }

      waMetricsGlobalHandler({url: (BEditor.environment.topWindow.contentarea ? BEditor.environment.topWindow.contentarea : BEditor.environment.topWindow).location.href, module: 'Editor', operation: 'ToolbarButtonClick', toolbarName: pToolbarConfig.name, command: command});
    }

    pToolbar.refreshState();
  };


  function execBasicCommand(pButton, command, args = {})
  {
    if (pThis.isObjValid && pThis.isObjValid > 1)
    {
      if (!isIE)
      {
        iFrameDoc.execCommand('styleWithCSS', false, false);
      }

      helpers.fixers.fixWebKitSpan(true);

      if (typeof(args.beforeCallback) === 'function')
      {
        Array.isArray(args.beforeArgs) ? args.beforeCallback(...args.beforeArgs) : args.beforeCallback();
      }

      if (isWebKit)
      {
        removeWaContentMarker();
      }

      iFrameDoc.execCommand(command, false, null);

      helpers.fixers.fixWebKitSpan(false);

      if (typeof(args.afterCallback) === 'function')
      {
        Array.isArray(args.afterArgs) ? args.afterCallback(...args.afterArgs) : args.afterCallback();
      }

      if (isWebKit)
      {
        setWaContentMarker();
      }

      if (args.useBookmark)
      {
        range.restoreBookmark();
      }

      contentChecker.setExcludeWordCheck();
    }
  }


  function findParentAbsElement (element)
  {
    while (element && element.nodeName.toUpperCase() !== 'BODY')
    {
      if ((element.nodeName.toUpperCase() === 'DIV' || element.nodeName.toUpperCase() === 'IMG') && $Style(element, 'position').toLowerCase() === 'absolute')
      {
        return element;
      }

      element = element.parentNode;
    }

    return null;
  }


  function moveParagraphCursorBeforeSpace ()
  {
    let currentSelection,
      currentRange,
      currentNode;

    currentSelection = range.selection.getCurrent();
    currentRange = range.getCurrent();

    if (currentSelection && currentSelection.type.toUpperCase() === 'TEXT' && currentRange && currentRange.htmlText === '')
    {
      currentNode = currentRange.parentElement();

      if (currentNode && currentNode.nodeName.toUpperCase() === 'P' && currentNode.innerHTML.substr(0, 6).toLowerCase() === '&nbsp;')
      {
        range.setCursorPoint(currentNode, true);
      }
    }
  }


  function removeAttributeCurrentElements ()
  {
    let i,
      elements = iFrameDoc.body.querySelectorAll('IMG[data-wacurrentarttextpicture]');

    for (i = elements.length; i--;)
    {
      if (elements[i] && elements[i].parentNode)
      {
        elements[i].removeAttribute('data-wacurrentarttextpicture');
      }
    }

    elements = iFrameDoc.body.querySelectorAll('IMG[data-wacurrentimage]');

    for (i = elements.length; i--;)
    {
      if (elements[i] && elements[i].parentNode)
      {
        elements[i].removeAttribute('data-wacurrentimage');
      }
    }

    elements = iFrameDoc.body.querySelectorAll('SPAN[data-wainsertpointer]');

    for (i = elements.length; i--;)
    {
      if (elements[i] && elements[i].parentNode)
      {
        elements[i].parentNode.removeChild(elements[i]);
      }
    }
  }


  function openMoreLocalDropdown (pButton)
  {
    pDropdown = pButton.getDropdown();

    if (!pDropdown)
    {
      return false;
    }

    if (pDropdown.getState())
    {
      closeDropdown(pButton);
    }
    else
    {
      openDropdown(pButton, pDropdown, 'moreLocalHandler');
    }
  }


  function moreLocalHandler (pButton)
  {
    let pParameters, tCommand;

    pDropdown = pButton.getDropdown();

    if (pDropdown)
    {
      pParameters = pDropdown.getReturnedParameters();

      if (pParameters && pParameters.selectedStyleId)
      {
        tCommand = pParameters.selectedStyleId;
      }
    }

    closeDropdown(pButton);

    if (tCommand)
    {
      pThis.execCommand(pButton, tCommand);
    }
  }


  this.validateBeforePublish = function()
  {
    if (style.normalizer.checkMsHTML())
    {
      style.normalizer.clearMsHTML(content.getHtmlData());
    }
  };





  function isChildrenOnlyTextNode (element)
  {
    let i,
        nodes = element.childNodes,
        isTextNode = null;

    if (nodes && nodes.length > 0)
    {
      isTextNode = true;

      for (i = 0; i < nodes.length; i++)
      {
        if (nodes[i].nodeType !== 3)
        {
          isTextNode = false;
          break;
        }
      }
    }

    return isTextNode;
  }


  function isOnlyTargetChildNode (parentElement, targetNode)
  {
    let i,
        reWhitespace = /\s/g,
        nodes = parentElement.childNodes,
        ret = false;

    if (nodes.length === 1)
    {
      if (nodes[0] === targetNode)
      {
        ret = true;
      }
    }
    else
    {
      ret = true;

      for (i = 0; i < nodes.length; i++)
      {
        if (nodes[i].nodeType === 1)
        {
          if (nodes[i] !== targetNode)
          {
            ret = false;
            break;
          }
        }
        else if (nodes[i].nodeType === 3)
        {
          if (nodes[i].textContent.replace(/\s/g, '') !== '')
          {
            ret = false;
            break;
          }
        }
      }
    }

    return ret;
  }


  function openDropdown (pButton, pDropdown, execCommand, args)
  {
    let element;

    if (setIsDropdownOpenTM) clearTimeout(setIsDropdownOpenTM);

    range.save(false, true);

    pDropdown.openDropdown(function() { pThis.execCommand(pButton, execCommand); }, function() { closeDropdown(pButton); }, function() { pThis.closeDropdownWait(pButton); }, function() { pThis.stopCloseDropdown(pButton); }, null, null, null, null, args);
    isDropdownOpen = true;

    if (isIE)
    {
      setTimeout(function () { pThis.stopCloseDropdown(pButton); }, 300);
    }
  }


  this.closeDropdownWait = function(pButton)
  {
    if (pButton)
    {
      pDropdown = pButton.getDropdown();
    }
    else if (pDropdown)
    {
      pButton = pDropdown.pButton;
    }

    if (!pButton || !pDropdown)
    {
      return false;
    }

    if (pButton.editorCloseDropdownTM) clearTimeout(pButton.editorCloseDropdownTM);

    pButton.editorCloseDropdownTM = setTimeout(function()
    {
      if (pDropdown.getState())
      {
        closeDropdown(pButton);
      }
    }, 333);
  };


  this.stopCloseDropdown = function(pButton)
  {
    if (pButton.editorCloseDropdownTM) clearTimeout(pButton.editorCloseDropdownTM);
  };


  function closeDropdown(pButton)
  {
    if (pButton)
    {
      pDropdown = pButton.getDropdown();
    }
    else if (pDropdown)
    {
      pButton = pDropdown.pButton;
    }

    if (!pButton || !pDropdown)
    {
      return false;
    }

    if (pButton.editorCloseDropdownTM) clearTimeout(pButton.editorCloseDropdownTM);

    pDropdown.closeDropdown();

    pThis.focus();
    range.restore(null, null, true);

    if (isIE && pButton.id && pButton.id.indexOf('_Table') !== -1)
    {
      if (setIsDropdownOpenTM) clearTimeout(setIsDropdownOpenTM);
      setIsDropdownOpenTM = setTimeout(function ()
      {
        isDropdownOpen = false;

        if (!window.BonaDialog.isDialogOpened)
        {
          pThis.focus();
          stopBluredEvent = false;
        }
      }, 500);
    }
    else
    {
      isDropdownOpen = false;

      if (!window.BonaDialog.isDialogOpened)
      {
        stopBluredEvent = false;
      }
    }
  }


  function setIsDropdownOpen (isOpen)
  {
    isDropdownOpen = isOpen;
  }


  function afterOpenDialog ()
  {
    stopBluredEvent = true;
    setTimeout(function() { pauseEditing(true); }, 10);
    BEditor.stopDragDropUploaderWatch();
  }


  function closeDialog(sender, args)
  {
    pDialog = pDialog || sender || null;

    if (pDialog)
    {
      if (pDialog.closeWindow)
      {
        pDialog.closeWindow();
      }
      else if (pDialog.hide)
      {
        pDialog.hide();
      }
    }

    afterCloseDialog();
  }


  function afterCloseDialog ()
  {
    pDialog = null;

    if (!isIE)
    {
      removeAttributeCurrentElements();
    }

    if (isWebKit)
    {
      isPersistEditing = true;
      pThis.focus();
      range.restore();
      setTimeout(function ()
      {
        pThis.focus();
        range.restore();
        stopBluredEvent = false;
        isPersistEditing = false;
      }, 250);
    }
    else
    {
      pThis.focus();
      range.restore();
      stopBluredEvent = false;
    }
  }





  this.getBodyId = function()
  {
    return iFrameDoc.body.id;
  };


  this.getBodyClass = function()
  {
    return iFrameDoc.body.className.replace(/(.*?)\s?controlTablesBordersInEditor(.*?)/, '$1$2');
  };


  function onlykeyCutCopyPaste(pButton, command)
  {
    let messages =
    {
      'cut': 'Please use Ctrl+X to Cut',
      'copy': 'Please use Ctrl+C to Copy',
      'paste': 'Please use Ctrl+V to Paste'
    };

    alert(messages[command]);
  }


  function setEnabledCommand (state, args = {})
  {
    let exclude = args.exclude || {},
      k;

    for (k in hEnabledCommand)
    {
      if (!(k in exclude))
      {
        hEnabledCommand[k].enabled = state;
      }
    }
  }


  let prevEnabledCommandState = {};

  this.queryCommandEnabled = function (pButton, command)
  {
    if (!pThis.isObjValid || pThis.isObjValid < 2)
    {
      return false;
    }

    if (isIE && ((pToolbar && pToolbar.isMouseDown) || isDropdownOpen) && prevEnabledCommandState[command] != null)
    {
      return prevEnabledCommandState[command];
    }

    let i,
      ret = false;

    if (hEnabledCommand[command])
    {
      if (hEnabledCommand[command].enabled)
      {
        if (hEnabledCommand[command].handlers)
        {
          for (i = 0; i < hEnabledCommand[command].handlers.length; i++)
          {
            if (!hEnabledCommand[command].handlers[i].handler(pButton, hEnabledCommand[command].handlers[i].params))
            {
              break;
            }
          }

          ret = (i === hEnabledCommand[command].handlers.length);
        }
        else
        {
          ret = true;
        }
      }
      else
      {
        ret = false;
      }
    }
    else
    {
      ret = false;
    }

    prevEnabledCommandState[command] = ret;
    return ret;
  };


  function queryBasicCommandEnabled(pButton, command)
  {
    let ret = false;

    if (isIE && ((pToolbar && pToolbar.isMouseDown) || isDropdownOpen) && prevEnabledCommandState[command] != null)
    {
      return prevEnabledCommandState[command];
    }

    if (pThis.isObjValid && pThis.isObjValid > 1)
    {
      try
      {
        ret = iFrameDoc.queryCommandEnabled(command);
      }
      catch (err)
      {
        ret = false;
      }
    }

    prevEnabledCommandState[command] = ret;
    return ret;
  }


  this.queryCommandState = function(pButton, command)
  {
    if (!pThis.isObjValid || pThis.isObjValid < 2)
      return false;

    let i;
    let ret = true;

    if (hStateCommand[command])
    {
      if (hStateCommand[command].handler)
      {
        return hStateCommand[command].handler(pButton, hStateCommand[command].params);
      }
      else if (hStateCommand[command].length)
      {
        for (i = 0; i < hStateCommand[command].length; i++)
        {
          ret = hStateCommand[command][i].handler(pButton, hStateCommand[command][i].params);

          if (!ret)
            return false;
        }

        return true;
      }
    }
  };


  function queryBasicCommandState(pButton, command)
  {
    if (pThis.isObjValid && pThis.isObjValid > 1)
    {
      let ret = false;

      try
      {
        ret = iFrameDoc.queryCommandState(command);

        if (isWebKit && ret && image.isImageSelected())
        {
          ret = false;
        }
      }
      catch (err)
      {
        return false;
      }

      return ret;
    }
  }


  this.hasFocus = function()
  {
    return isFocused;
  };


  function addHandler(eventName, handler, handlerName)
  {
    let i;
    let pHandler = null;
    handlerName = (handlerName) ? handlerName : null;

    if (!handlerList[eventName])
    {
      return false;
    }

    if (handlerName)
    {
      for (i = 0; i < handlerList[eventName].length; i++)
      {
        if (handlerList[eventName][i].handlerName && handlerList[eventName][i].handlerName === handlerName)
        {
          break;
        }
      }

      pHandler = i;
    }
    else
    {
      pHandler = handlerList[eventName].length;
    }

    if (!handlerList[eventName][pHandler])
    {
      handlerList[eventName][pHandler] = {};
    }

    handlerList[eventName][pHandler].handlerName = handlerName;
    handlerList[eventName][pHandler].handler = handler;

    return true;
  };


  function removeHandler(eventName, handlerName)
  {
    let i;

    if (!eventName || !handlerName)
    {
      return false;
    }

    for (i = 0; i < handlerList[eventName].length; i++)
    {
      if (handlerList[eventName][i].handlerName && handlerList[eventName][i].handlerName === handlerName)
      {
        break;
      }
    }

    handlerList[eventName].splice(i, 1);
  };


  function fireEvent(eventName, params)
  {
    let i;

    if (!handlerList[eventName])
    {
      return false;
    }

    for (i = 0; i < handlerList[eventName].length; i++)
    {
      handlerList[eventName][i].handler(editorId, pThis, eventName, params);
    }

    return true;
  }


  function unload ()
  {
    if (setObjValidTM) clearTimeout(setObjValidTM);

    let htmlContent = content.getData().htmlContent;

    pThis.isObjExist = false;

    if (pParent === 'MultiEditor')
    {
      pParent.unloadedEditor(pThis);
    }

    pThis.isObjValid = 2;

    let i;

    if (layer.objectFactory)
    {
      layer.objectFactory.unload();
    }

    frameSize.dispose();
    range.stopCheckSelection();

    if (checkInsertedContainerTM) clearInterval(checkInsertedContainerTM);
    if (setIsDropdownOpenTM) clearTimeout(setIsDropdownOpenTM);
    if (setIOSCheckRefreshStateTM) clearTimeout(setIOSCheckRefreshStateTM);

    if (checkContentTM)
    {
      clearInterval(checkContentTM);
      checkContentTM = null;
    }

    copyPaste.hideAutoCleaningMessage();

    if (pDialog)
    {
      if (pDialog.closeWindow)
      {
        pDialog.closeWindow();
      }
      else if (pDialog.hide)
      {
        pDialog.hide();
      }
    }

    pDialog = null;
    isDropdownOpen = false;

    if (pToolbar)
    {
      pToolbar.unregEditorInstanceDo(pThis);
      isFocused = false;
      pToolbar.refreshState();
    }

    pThis.isObjValid = false;
    pThis.EditorStopped.fireHandlers({ 'editorContent': htmlContent });

    try
    {
      if (isIE)
      {
        iFrame.detachEvent('onunload', pThis.unload);

        BEditor.domHandlers.removeHandler(BEditor.environment.topWindow.document, 'click', prePauseEditing);

        if (BEditor.environment.topWindow.contentarea)
        {
          BEditor.domHandlers.removeHandler(BEditor.environment.topWindow.contentarea.document, 'click', prePauseEditing);
        }
      }
      else
      {
        iFrameDoc.removeEventListener('unload', pThis.unload, false);
      }
    }
    catch (err) {}

    setTimeout.disposeTimeouts();
  }


  function deleteEditor()
  {
    if (objIFrame)
    {
      try
      {
        if (objIFrame.parentNode)
        {
          objIFrame.parentNode.removeChild(objIFrame);
        }
        else if ('removeNode' in objIFrame)
        {
          objIFrame.removeNode(true);
        }
      }
      catch (err) {}

      objIFrame = null;
    }

    if (cutBox)
    {
      try
      {
        if (cutBox.parentNode)
        {
          cutBox.parentNode.removeChild(cutBox);
        }
      }
      catch (err) {}

      cutBox = null;
    }

    if (inContainer)
    {
      try
      {
        inContainer.style.display = 'block';

        if (inContainer.id && inContainer.id.indexOf('_editableArea_waEditorTmpId') !== -1)
        {
          inContainer.removeAttribute('id');
        }
      }
      catch (err) {}
    }
  }


  function disposeEditor ()
  {
    BEditor.AdminPanel.ContentAreaUnload.removeHandler(disposeEditor);
    setTimeout(disposeEditorDo, 100);
  }


  function disposeEditorDo ()
  {
    unload();
    deleteEditor();
    previousInContainer = null;

    pThis.isObjDeleted = true;
  }


  this.restoreAttributeForDragDropDo = function ()
  {
    let i, key;
    let tags = iFrameDoc.body.getElementsByTagName('IMG');

    for (i = 0; i < tags.length; i++)
    {
      if (tags[i].src && tags[i].src.indexOf('modulecupforwebkit') !== -1 && tags[i].getAttribute('data-modulexml', 0) == null)
      {
        for (key in BEditor.EditorsManager.Sys.savedAttributesForDragDrop)
        {
          if (key.toLowerCase() !== 'src')
          {
            tags[i].setAttribute(key, BEditor.EditorsManager.Sys.savedAttributesForDragDrop[key], 0);
          }
        }

        break;
      }
    }
  };


  pThis.isObjValid = 0;
}
